import React, { useState, useEffect, useRef, useMemo } from 'react';
import { db, storage, functions } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { useAuthorDashboard } from '../AuthorDashboardContext';
import {
  doc, setDoc, updateDoc, deleteDoc, addDoc, collection,
  getDocs, query, where, orderBy, limit as firestoreLimit,
  serverTimestamp, increment, onSnapshot, getDoc
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { parseReaderFromEmail } from '../../anthropic';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import ReadingHistoryImport from '../../ReadingHistoryImport';
import ReaderResume from '../../ReaderResume';
import KindleMissingDialog from '../../components/KindleMissingDialog';
import {
  Search, Star, BookOpen, CheckCircle, XCircle, Mail, User,
  Plus, Minus, Edit2, Trash2, Download, Upload, Filter,
  ExternalLink, Gift, Lock, Send, Paperclip, X, Clock,
  AlertTriangle, CheckSquare
} from 'lucide-react';

/* ----------------------------------------
   STAT CARD
---------------------------------------- */
function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <Card className="rounded-2xl border border-slate-100 bg-gradient-to-br from-white via-slate-50/60 to-slate-100/40 shadow-sm">
      <CardContent className="flex flex-col gap-2 px-4 py-3">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-slate-500">{label}</p>
          <div className={`flex h-8 w-8 items-center justify-center rounded-full ${accent}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
        <p className="text-2xl font-semibold text-slate-900">{value}</p>
      </CardContent>
    </Card>
  );
}

/* ----------------------------------------
   SOURCE BADGE COMPONENT
---------------------------------------- */
function SourceBadge({ source }) {
  if (!source) return null;
  const badges = {
    'reader_portal': { label: 'Portal', color: 'bg-purple-100 text-purple-700' },
    'manual': { label: 'Manual', color: 'bg-slate-100 text-slate-700' },
  };
  const badgeInfo = badges[source] || { label: 'Manual', color: 'bg-slate-100 text-slate-700' };
  return (
    <Badge className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-medium border-0 ${badgeInfo.color}`}>
      <User className="h-2.5 w-2.5" />
      {badgeInfo.label}
    </Badge>
  );
}

/* ----------------------------------------
   RESOLVE KINDLE EMAIL
   Per-book reader docs may have stale/empty eReaderEmail
   if the backfill ran before the Kindle email was entered.
   Fall back to the global readers collection (same data
   source App.js CRM uses) via a lookup map.
---------------------------------------- */
function getKindleEmail(reader, globalKindleMap) {
  // 1. Check per-book reader doc fields
  const e = (reader.eReaderEmail || '').trim();
  if (e) return e;
  const k = (reader.kindleEmail || '').trim();
  if (k) return k;
  // 2. Fall back to global reader's eReaderEmail via lookup map
  if (globalKindleMap) {
    const byMaster = globalKindleMap[reader.masterReaderId];
    if (byMaster) return byMaster;
    const byId = globalKindleMap[reader.id];
    if (byId) return byId;
    const byEmail = reader.emailCorrespondence && globalKindleMap[reader.emailCorrespondence.toLowerCase()];
    if (byEmail) return byEmail;
  }
  return '';
}

/* ----------------------------------------
   EREADER EMAIL STATUS ICON
---------------------------------------- */
function EReaderEmailIcon({ emailCorrespondence, eReaderEmail }) {
  const ereaderEmail = (eReaderEmail || '').trim();
  const correspondenceEmail = (emailCorrespondence || '').toLowerCase();
  const isKindle = ereaderEmail.toLowerCase().endsWith('@kindle.com');

  if (!ereaderEmail) {
    return (
      <span className="inline-flex items-center justify-center rounded-full bg-red-100 p-1" title="No Kindle email — cannot receive ARC">
        <XCircle className="h-3 w-3 text-red-600" />
      </span>
    );
  }
  if (!isKindle) {
    return (
      <span className="inline-flex items-center justify-center rounded-full bg-orange-100 p-1" title="Not a @kindle.com address — cannot receive ARC via Kindle delivery">
        <AlertTriangle className="h-3 w-3 text-orange-600" />
      </span>
    );
  }
  if (ereaderEmail.toLowerCase() === correspondenceEmail) {
    return (
      <span className="inline-flex items-center justify-center rounded-full bg-yellow-100 p-1" title="Kindle email same as correspondence — verify this is correct">
        <Mail className="h-3 w-3 text-yellow-600" />
      </span>
    );
  }
  return (
    <span className="inline-flex items-center justify-center rounded-full bg-green-100 p-1" title="Valid @kindle.com email">
      <CheckCircle className="h-3 w-3 text-green-600" />
    </span>
  );
}

/* ----------------------------------------
   STATUS COLOUR MAP  (matches CRM App.js)
---------------------------------------- */
const getStatusColor = (status) => {
  const colors = {
    'Noted ARC interest': 'bg-blue-100 text-blue-700',
    'Engaged': 'bg-purple-100 text-purple-700',
    'Follow-up needed': 'bg-yellow-100 text-yellow-700',
    'Need follow up': 'bg-yellow-100 text-yellow-700',
    'Accepted': 'bg-teal-100 text-teal-700',
    'ARC sent': 'bg-green-100 text-green-700',
    'Reviewed': 'bg-emerald-100 text-emerald-700',
    'Closed': 'bg-slate-100 text-slate-500',
    'Street Team': 'bg-slate-100 text-teal-700',
    'Street team': 'bg-slate-100 text-teal-700',
    'Book 2 ARC interest': 'bg-indigo-100 text-indigo-700',
    'New Buyer': 'bg-orange-100 text-orange-700',
  };
  return colors[status] || 'bg-slate-100 text-slate-600';
};

/* ----------------------------------------
   COMPUTE DISPLAY STATUS
   Priority:
   1. If arcStatus/status has a real CRM value → show it
   2. If source = "manual" → "Noted ARC interest"
   3. If portal signup + no Kindle email → "Need follow up"
   4. If portal signup + has Kindle email → "Engaged"
---------------------------------------- */
const REAL_STATUSES = new Set([
  'Accepted', 'ARC sent', 'Reviewed', 'Street Team', 'Street team',
  'Book 2 ARC interest', 'Closed', 'Noted ARC interest',
  'Engaged', 'Follow-up needed', 'Need follow up', 'New Buyer',
]);

function getDisplayStatus(reader) {
  // Priority 1: real CRM status value
  const status = reader.arcStatus || reader.status;
  if (status && REAL_STATUSES.has(status)) return status;

  // Priority 2: manually added readers
  if (reader.source === 'manual') return 'Noted ARC interest';

  // Priority 3 & 4: portal signups — check Kindle email
  const hasKindleEmail = getKindleEmail(reader) !== '';
  if (!hasKindleEmail) return 'Need follow up';
  return 'Engaged';
}

/* ----------------------------------------
   BLANK READER TEMPLATE
---------------------------------------- */
const blankReader = {
  name: '',
  emailCorrespondence: '',
  eReaderEmail: '',
  acceptsMarketing: true,
  interestedIn: 'eBook ARC request',
  reviewPlatforms: [],
  socialUsernames: '',
  favoriteBooks: '',
  location: '',
  signupDate: new Date().toISOString().split('T')[0],
  status: 'Noted ARC interest',
  arcSent: '',
  reviewDate: '',
  rating: '',
  notes: '',
  totalPoints: 0,
  totalReviews: 0,
  booksReceived: [],
  primaryPlatform: 'Instagram',
  followerCount: 0,
};

/* ========================================
   MAIN COMPONENT
======================================== */
export default function AuthorReadersTab({ onMessageReader }) {
  const { user } = useAuth();
  const { readers, books, campaigns, reviews, rewards, basePath, bookApplications } = useAuthorDashboard();

  // Build lookup map from global readers (same collection App.js CRM uses)
  // Maps reader id and email → eReaderEmail so per-book readers can fall back to it
  const globalKindleMap = useMemo(() => {
    const map = {};
    for (const r of readers) {
      const email = (r.eReaderEmail || r.kindleEmail || '').trim();
      if (!email) continue;
      if (r.id) map[r.id] = email;
      if (r.emailCorrespondence) map[r.emailCorrespondence.toLowerCase()] = email;
    }
    return map;
  }, [readers]);

  const fileInputRef = useRef(null);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterSource, setFilterSource] = useState('All');
  const [filterArc, setFilterArc] = useState('All');
  const [filterEReader, setFilterEReader] = useState('All');
  const [filterRating, setFilterRating] = useState('All');
  const [filterBook, setFilterBook] = useState('All');

  // Collapse state for book sections
  const [collapsedBooks, setCollapsedBooks] = useState({});

  // Reader edit drawer
  const [showReaderDrawer, setShowReaderDrawer] = useState(false);
  const [editingReader, setEditingReader] = useState(null);
  const [editingBookId, setEditingBookId] = useState(null);
  const [readerForm, setReaderForm] = useState(blankReader);
  const [isSavingReader, setIsSavingReader] = useState(false);

  // Reader detail panel
  const [viewingReader, setViewingReader] = useState(null);
  const [showReaderDetailPanel, setShowReaderDetailPanel] = useState(false);
  const [readerReviews, setReaderReviews] = useState([]);
  const [readerReadingHistory, setReaderReadingHistory] = useState([]);
  const [showReaderHistoryImport, setShowReaderHistoryImport] = useState(false);
  const [readerImportBatches, setReaderImportBatches] = useState([]);

  // Points / rewards
  const [pointsAmount, setPointsAmount] = useState('');
  const [pointsReason, setPointsReason] = useState('');
  const [pointsHistory, setPointsHistory] = useState([]);
  const [selectedRewardId, setSelectedRewardId] = useState('');

  // Quick add modal
  const [showQuickAddModal, setShowQuickAddModal] = useState(false);
  const [pastedEmailText, setPastedEmailText] = useState('');
  const [isParsingEmail, setIsParsingEmail] = useState(false);

  // Send ARC state
  const [selectedReaderIds, setSelectedReaderIds] = useState(new Set());
  const [showSendArcModal, setShowSendArcModal] = useState(false);
  const [arcBookId, setArcBookId] = useState('');
  const [arcEpubFile, setArcEpubFile] = useState(null);
  const [arcSubject, setArcSubject] = useState('');
  const [arcBody, setArcBody] = useState('');
  const [isSendingArc, setIsSendingArc] = useState(false);
  const [arcUploadProgress, setArcUploadProgress] = useState('');
  const [arcSendProgress, setArcSendProgress] = useState(null); // { sent, failed, skipped, total, current }
  const [kindleMissingPrompt, setKindleMissingPrompt] = useState(null); // null | { skippedNames, onProceed }
  const epubInputRef = useRef(null);

  // bookApplications now lives in AuthorDashboardContext; consumed via useAuthorDashboard above.

  // Track which waitlist application messages are expanded inline
  const [expandedAppMessages, setExpandedAppMessages] = useState(new Set());

  // Per-book readers: { bookId: [reader1, reader2, ...] }
  const [perBookReaders, setPerBookReaders] = useState({});

  useEffect(() => {
    if (!basePath || !books.length) {
      setPerBookReaders({});
      return;
    }
    const unsubs = books.map(book =>
      onSnapshot(
        collection(db, basePath, 'books', book.id, 'readers'),
        (snap) => {
          const items = [];
          snap.forEach(d => items.push({ id: d.id, ...d.data(), _bookId: book.id }));
          setPerBookReaders(prev => ({ ...prev, [book.id]: items }));
        }
      )
    );
    return () => unsubs.forEach(u => u());
  }, [basePath, books]);

  // Backfill: create per-book reader docs from campaign applications + existing booksReceived
  useEffect(() => {
    if (!basePath || !books.length) return;
    let cancelled = false;

    async function backfill() {
      for (const book of books) {
        // Check which readers already have a per-book doc
        let existingIds;
        try {
          const snap = await getDocs(collection(db, basePath, 'books', book.id, 'readers'));
          existingIds = new Set();
          snap.forEach(d => existingIds.add(d.id));
        } catch { continue; }
        if (cancelled) return;

        // 1. Backfill from campaign applications (interested, pending, waitlisted, approved)
        const apps = bookApplications[book.id] || [];
        for (const app of apps) {
          if (['interested', 'pending', 'waitlisted', 'approved'].includes(app.status)) {
            const docId = app.readerId || app.readerEmail;
            if (!docId || existingIds.has(docId)) continue;
            // Also try email as ID
            if (app.readerEmail && existingIds.has(app.readerEmail)) continue;
            const useId = app.readerId || app.readerEmail;
            // Look up the global reader for profile data
            let globalReader = null;
            if (app.readerId) {
              try {
                const gSnap = await getDoc(doc(db, 'readers', app.readerId));
                if (gSnap.exists()) globalReader = { id: gSnap.id, ...gSnap.data() };
              } catch { /* skip */ }
            }
            if (!globalReader && app.readerEmail) {
              try {
                const gSnap = await getDoc(doc(db, 'readers', app.readerEmail));
                if (gSnap.exists()) globalReader = { id: gSnap.id, ...gSnap.data() };
              } catch { /* skip */ }
            }
            if (cancelled) return;
            const statusMap = { interested: 'Noted ARC interest', pending: 'Noted ARC interest', waitlisted: 'Noted ARC interest', approved: 'Accepted' };
            try {
              await setDoc(doc(db, basePath, 'books', book.id, 'readers', useId), {
                name: globalReader?.name || app.readerName || '',
                emailCorrespondence: globalReader?.emailCorrespondence || app.readerEmail || '',
                eReaderEmail: globalReader?.eReaderEmail || '',
                kindleEmail: globalReader?.kindleEmail || '',
                source: globalReader?.source || 'reader_portal',
                acceptsMarketing: globalReader?.acceptsMarketing ?? true,
                status: app.status === 'approved' ? (globalReader?.status || 'Accepted') : statusMap[app.status] || 'Noted ARC interest',
                hasApprovedArcRequest: app.status === 'approved',
                arcSent: '', reviewDate: '', rating: '', reviewRating: '',
                totalPoints: 0, totalReviews: 0,
                signupDate: globalReader?.signupDate || new Date().toISOString().split('T')[0],
                notes: '',
                interestedIn: globalReader?.interestedIn || 'eBook ARC request',
                reviewPlatforms: globalReader?.reviewPlatforms || [],
                socialUsernames: globalReader?.socialUsernames || '',
                primaryPlatform: globalReader?.primaryPlatform || 'Instagram',
                followerCount: globalReader?.followerCount || 0,
                location: globalReader?.location || '',
                favoriteBooks: globalReader?.favoriteBooks || '',
                bookId: book.id,
                masterReaderId: globalReader?.id || useId,
                applicationStatus: app.status,
                createdAt: serverTimestamp(),
                lastModified: serverTimestamp(),
              });
              existingIds.add(useId);
            } catch (err) {
              console.error(`[Readers] Backfill failed for ${useId} on book ${book.id}:`, err);
            }
          }
        }

        // 2. Backfill from global readers with booksReceived containing this book
        for (const reader of readers) {
          if (!(reader.booksReceived || []).includes(book.id)) continue;
          // Check both possible IDs
          if (existingIds.has(reader.id)) continue;
          if (reader.emailCorrespondence && existingIds.has(reader.emailCorrespondence)) continue;
          if (cancelled) return;
          try {
            await setDoc(doc(db, basePath, 'books', book.id, 'readers', reader.id), {
              name: reader.name || '',
              emailCorrespondence: reader.emailCorrespondence || '',
              eReaderEmail: reader.eReaderEmail || '',
              kindleEmail: reader.kindleEmail || '',
              source: reader.source || 'manual',
              acceptsMarketing: reader.acceptsMarketing ?? true,
              status: reader.status || 'Noted ARC interest',
              hasApprovedArcRequest: reader.hasApprovedArcRequest || false,
              arcSent: reader.arcSent || '', reviewDate: reader.reviewDate || '',
              rating: reader.rating || '', reviewRating: reader.reviewRating || '',
              totalPoints: reader.totalPoints || 0, totalReviews: reader.totalReviews || 0,
              signupDate: reader.signupDate || '',
              notes: reader.notes || '',
              interestedIn: reader.interestedIn || 'eBook ARC request',
              reviewPlatforms: reader.reviewPlatforms || [],
              socialUsernames: reader.socialUsernames || '',
              primaryPlatform: reader.primaryPlatform || 'Instagram',
              followerCount: reader.followerCount || 0,
              location: reader.location || '',
              favoriteBooks: reader.favoriteBooks || '',
              bookId: book.id,
              masterReaderId: reader.id,
              createdAt: serverTimestamp(),
              lastModified: serverTimestamp(),
            });
            existingIds.add(reader.id);
          } catch (err) {
            console.error(`[Readers] Backfill from global failed for ${reader.id} on book ${book.id}:`, err);
          }
        }
      }
    }

    backfill();
    return () => { cancelled = true; };
  }, [basePath, books, bookApplications, readers]);

  // Helper: apply search/status filters to a list of readers
  function applyReaderFilters(readerList) {
    return readerList.filter((r) => {
      const term = searchTerm.toLowerCase();
      const match =
        r.name?.toLowerCase().includes(term) ||
        r.emailCorrespondence?.toLowerCase().includes(term) ||
        r.socialUsernames?.toLowerCase().includes(term) ||
        (Array.isArray(r.reviewPlatforms) ? r.reviewPlatforms.join(' ').toLowerCase().includes(term) : false);
      const matchStatus = filterStatus === 'All' || getDisplayStatus(r) === filterStatus;
      const matchSource = filterSource === 'All' || r.source === filterSource;
      const matchArc =
        filterArc === 'All' ||
        (filterArc === 'Has ARC' && r.hasApprovedArcRequest) ||
        (filterArc === 'No ARC' && !r.hasApprovedArcRequest);
      const kindle = getKindleEmail(r, globalKindleMap);
      const matchEReader =
        filterEReader === 'All' ||
        (filterEReader === 'Has Email' && kindle !== '') ||
        (filterEReader === 'Missing' && kindle === '') ||
        (filterEReader === 'Duplicate' && kindle && kindle.toLowerCase() === (r.emailCorrespondence || '').toLowerCase());
      const matchRating =
        filterRating === 'All' ||
        (filterRating === '5' && Number(r.reviewRating) === 5) ||
        (filterRating === '4' && Number(r.reviewRating) === 4) ||
        (filterRating === '3' && Number(r.reviewRating) === 3) ||
        (filterRating === 'unrated' && !r.reviewRating);
      return match && matchStatus && matchSource && matchArc && matchEReader && matchRating;
    });
  }

  /* ─── Filtered Readers ──── */
  const filteredReaders = readers.filter((r) => {
    const term = searchTerm.toLowerCase();
    const match =
      r.name?.toLowerCase().includes(term) ||
      r.emailCorrespondence?.toLowerCase().includes(term) ||
      r.socialUsernames?.toLowerCase().includes(term) ||
      (Array.isArray(r.reviewPlatforms)
        ? r.reviewPlatforms.join(' ').toLowerCase().includes(term)
        : false);

    const matchStatus = filterStatus === 'All' || getDisplayStatus(r) === filterStatus;
    const matchSource = filterSource === 'All' || r.source === filterSource;
    const matchArc =
      filterArc === 'All' ||
      (filterArc === 'Has ARC' && r.hasApprovedArcRequest) ||
      (filterArc === 'No ARC' && !r.hasApprovedArcRequest);
    const kindle2 = getKindleEmail(r, globalKindleMap);
    const matchEReader =
      filterEReader === 'All' ||
      (filterEReader === 'Has Email' && kindle2 !== '') ||
      (filterEReader === 'Missing' && kindle2 === '') ||
      (filterEReader === 'Duplicate' && kindle2 && kindle2.toLowerCase() === (r.emailCorrespondence || '').toLowerCase());
    const matchRating =
      filterRating === 'All' ||
      (filterRating === '5' && Number(r.reviewRating) === 5) ||
      (filterRating === '4' && Number(r.reviewRating) === 4) ||
      (filterRating === '3' && Number(r.reviewRating) === 3) ||
      (filterRating === 'unrated' && !r.reviewRating);

    return match && matchStatus && matchSource && matchArc && matchEReader && matchRating;
  });

  /* ─── Stats ──── */
  const allPerBookReaders = Object.values(perBookReaders).flat();
  const approvedReviews = reviews.filter((r) => r.status === 'approved');
  const approvedReviewsWithRating = approvedReviews.filter((r) => r.rating);
  // Deduplicate by email for unique reader count
  const uniqueEmails = new Set(allPerBookReaders.map(r => r.emailCorrespondence?.toLowerCase()).filter(Boolean));
  const stats = {
    totalReaders: uniqueEmails.size || readers.length,
    arcsSent: allPerBookReaders.filter((r) => {
      const ds = getDisplayStatus(r);
      return ['ARC sent', 'Reviewed', 'Street Team', 'Street team', 'Book 2 ARC interest'].includes(ds);
    }).length,
    reviewed: approvedReviews.length,
    avgRating: approvedReviewsWithRating.length > 0
      ? (approvedReviewsWithRating.reduce((sum, r) => sum + Number(r.rating || 0), 0) / approvedReviewsWithRating.length).toFixed(1)
      : 'N/A',
  };

  /* ─── Drawer open/close ──── */
  const openNewReaderDrawer = (bookId) => {
    setEditingReader(null);
    setEditingBookId(bookId || (filterBook !== 'All' ? filterBook : books[0]?.id || null));
    setReaderForm(blankReader);
    setShowReaderDrawer(true);
  };

  const openEditReaderDrawer = (reader, bookId) => {
    // Diagnostic: log the current Firestore path and document structure
    const effectiveBookId = bookId || reader._bookId || null;
    console.log('[AuthorReadersTab] openEditReaderDrawer:', {
      path: effectiveBookId && basePath
        ? `${basePath}/books/${effectiveBookId}/readers/${reader.id}`
        : `readers/${reader.id} (global)`,
      readerDoc: { id: reader.id, name: reader.name, emailCorrespondence: reader.emailCorrespondence, booksReceived: reader.booksReceived, _bookId: reader._bookId, masterReaderId: reader.masterReaderId, bookId: reader.bookId },
      perBookReaderCounts: Object.fromEntries(Object.entries(perBookReaders).map(([k, v]) => [k, v.length])),
    });
    setEditingReader(reader);
    setEditingBookId(effectiveBookId);
    setReaderForm({ ...blankReader, ...reader });
    setShowReaderDrawer(true);
  };

  const closeReaderDrawer = () => {
    setShowReaderDrawer(false);
    setEditingReader(null);
    setEditingBookId(null);
    setReaderForm(blankReader);
  };

  /* ─── Reader Detail Panel ──── */
  const openReaderDetailPanel = (reader) => {
    setViewingReader(reader);
    setShowReaderDetailPanel(true);
    setShowReaderHistoryImport(false);

    // Load reader's reviews
    const readerReviewsList = reviews.filter(
      r => r.readerEmail === reader.emailCorrespondence || r.readerId === reader.id
    );
    setReaderReviews(readerReviewsList);

    // Load points history
    loadPointsHistory(reader.id);

    // Load reading history + import batches
    loadReaderReadingHistory(reader.id);
  };

  const loadReaderReadingHistory = async (rid) => {
    setReaderReadingHistory([]);
    setReaderImportBatches([]);
    if (!rid) return;
    try {
      const [histSnap, batchSnap] = await Promise.all([
        getDocs(collection(db, 'readers', rid, 'readingHistory')),
        getDocs(collection(db, 'readers', rid, 'importBatches')),
      ]);
      const hist = [];
      histSnap.forEach(d => hist.push({ id: d.id, ...d.data() }));
      setReaderReadingHistory(hist);
      const batches = [];
      batchSnap.forEach(d => batches.push({ id: d.id, ...d.data() }));
      batches.sort((a, b) => (b.importedAt?.seconds || 0) - (a.importedAt?.seconds || 0));
      setReaderImportBatches(batches);
    } catch (e) {
      console.error('[AuthorReadersTab] Failed to load reading history:', e);
    }
  };

  const closeReaderDetailPanel = () => {
    setShowReaderDetailPanel(false);
    setViewingReader(null);
    setReaderReviews([]);
    setReaderReadingHistory([]);
    setReaderImportBatches([]);
  };

  const switchToEditFromDetail = () => {
    setShowReaderDetailPanel(false);
    openEditReaderDrawer(viewingReader);
  };

  /* ─── Load Points History ──── */
  const loadPointsHistory = async (readerId) => {
    try {
      const historyRef = collection(db, 'pointsHistory');
      const q = query(
        historyRef,
        where('readerId', '==', readerId),
        orderBy('createdAt', 'desc'),
        firestoreLimit(20)
      );
      const snapshot = await getDocs(q);
      const history = [];
      snapshot.forEach(d => { history.push({ id: d.id, ...d.data() }); });
      setPointsHistory(history);
    } catch (error) {
      setPointsHistory([]);
    }
  };

  /* ─── Save Reader ──── */
  async function handleSaveReader(e) {
    e.preventDefault();
    if (!user) return;
    if (!editingBookId) { alert('Please select a book for this reader.'); return; }
    if (!basePath) { alert('Author account not fully set up. Please check your profile.'); return; }
    setIsSavingReader(true);

    try {
      const data = {
        ...readerForm,
        lastModified: serverTimestamp(),
        authorId: user.uid,
        bookId: editingBookId,
      };
      // Remove fields that shouldn't be stored in per-book records
      delete data._bookId;
      delete data.booksReceived;

      const readerDocId = editingReader ? editingReader.id : readerForm.emailCorrespondence;
      const readerRef = doc(db, basePath, 'books', editingBookId, 'readers', readerDocId);

      if (editingReader) {
        await updateDoc(readerRef, data);
      } else {
        await setDoc(readerRef, {
          ...data,
          source: 'manual',
          createdAt: serverTimestamp(),
          masterReaderId: readerForm.emailCorrespondence,
        }, { merge: true });
      }

      // Sync to global reader's booksReceived
      const globalReader = readers.find(r =>
        r.id === readerDocId ||
        (r.emailCorrespondence && readerForm.emailCorrespondence &&
         r.emailCorrespondence.toLowerCase() === readerForm.emailCorrespondence.toLowerCase())
      );
      if (globalReader) {
        const currentBooks = globalReader.booksReceived || [];
        if (!currentBooks.includes(editingBookId)) {
          await updateDoc(doc(db, 'readers', globalReader.id), {
            booksReceived: [...currentBooks, editingBookId],
            lastModified: serverTimestamp(),
          });
        }
      }

      closeReaderDrawer();
      alert('Reader saved!');
    } catch (err) {
      alert('Error saving reader: ' + err.message);
    }
    setIsSavingReader(false);
  }

  /* ─── Delete Reader ──── */
  async function handleDeleteReader(id, bookId) {
    if (!bookId) {
      // Unassigned reader — delete from global collection
      if (!window.confirm('Delete this reader from the global list?')) return;
      try {
        await deleteDoc(doc(db, 'readers', id));
        alert('Reader deleted.');
      } catch (err) { alert('Error deleting reader: ' + err.message); }
      return;
    }
    if (!window.confirm('Remove this reader from this book? This only affects this book.')) return;
    try {
      if (basePath) {
        await deleteDoc(doc(db, basePath, 'books', bookId, 'readers', id));
      }
      // Also remove from global reader's booksReceived
      const pbReader = (perBookReaders[bookId] || []).find(r => r.id === id);
      const email = pbReader?.emailCorrespondence || '';
      const masterId = pbReader?.masterReaderId || id;
      const globalReader = readers.find(r =>
        r.id === id || r.id === masterId ||
        (r.emailCorrespondence && email && r.emailCorrespondence.toLowerCase() === email.toLowerCase())
      );
      if (globalReader) {
        const currentBooks = globalReader.booksReceived || [];
        if (currentBooks.includes(bookId)) {
          await updateDoc(doc(db, 'readers', globalReader.id), {
            booksReceived: currentBooks.filter(bId => bId !== bookId),
            lastModified: serverTimestamp(),
          });
        }
      }
      alert('Reader removed from this book.');
    } catch (err) {
      alert('Error removing reader: ' + err.message);
    }
  }

  /* ─── Points ──── */
  async function handleAddPoints(readerId, amount, reason, bookId) {
    if (!amount || amount <= 0) { alert('Please enter a valid points amount'); return; }
    if (!reason || reason.trim() === '') { alert('Please enter a reason for adding points'); return; }
    try {
      if (bookId) {
        await updateDoc(doc(db, basePath, 'books', bookId, 'readers', readerId), {
          totalPoints: increment(amount),
          lastModified: serverTimestamp(),
        });
      }
      await addDoc(collection(db, 'pointsHistory'), {
        readerId, bookId: bookId || '', amount, type: 'manual_add', reason,
        createdAt: serverTimestamp(), createdBy: user.email,
      });
      alert(`Added ${amount} points!`);
      setPointsAmount('');
      setPointsReason('');
    } catch (error) {
      alert('Error adding points: ' + error.message);
    }
  }

  async function handleDeductPoints(readerId, amount, reason, bookId) {
    if (!amount || amount <= 0) { alert('Please enter a valid points amount'); return; }
    if (!reason || reason.trim() === '') { alert('Please enter a reason for deducting points'); return; }
    try {
      const allReaders = Object.values(perBookReaders).flat();
      const reader = allReaders.find(r => r.id === readerId && r._bookId === bookId);
      if (reader && reader.totalPoints < amount) {
        if (!window.confirm(`Reader only has ${reader.totalPoints} points. Deduct ${amount} anyway (will result in negative balance)?`)) return;
      }
      if (bookId) {
        await updateDoc(doc(db, basePath, 'books', bookId, 'readers', readerId), {
          totalPoints: increment(-amount),
          lastModified: serverTimestamp(),
        });
      }
      await addDoc(collection(db, 'pointsHistory'), {
        readerId, bookId: bookId || '', amount: -amount, type: 'manual_deduct', reason,
        createdAt: serverTimestamp(), createdBy: user.email,
      });
      alert(`Deducted ${amount} points!`);
      setPointsAmount('');
      setPointsReason('');
    } catch (error) {
      alert('Error deducting points: ' + error.message);
    }
  }

  /* ─── Give Manual Reward ──── */
  async function handleGiveReward(readerId, rewardId) {
    if (!rewardId) { alert('Please select a reward'); return; }
    const reward = (rewards || []).find(r => r.id === rewardId);
    const allReaders = Object.values(perBookReaders).flat();
    const reader = allReaders.find(r => r.id === readerId);
    if (!reward || !reader) { alert('Reward or reader not found'); return; }
    if (!window.confirm(`Give "${reward.name}" to ${reader.name}? (This bypasses the normal redemption process)`)) return;
    try {
      await addDoc(collection(db, 'redemptions'), {
        readerId, readerEmail: reader.emailCorrespondence, readerName: reader.name,
        rewardId, rewardName: reward.name, pointsSpent: 0, status: 'fulfilled',
        redeemedAt: serverTimestamp(), fulfilledAt: serverTimestamp(),
        manualGrant: true, grantedBy: user.email, notes: 'Manually granted by admin',
      });
      if (reward.stock === 'limited' && reward.quantity > 0) {
        await updateDoc(doc(db, 'rewards', rewardId), { quantity: increment(-1) });
      }
      alert(`✅ Reward "${reward.name}" given to ${reader.name}!`);
      setSelectedRewardId('');
    } catch (error) {
      alert('❌ Error giving reward: ' + error.message);
    }
  }

  /* ─── Send ARC Email ──── */
  function openSendArcModal(preselectedReaderIds) {
    const ids = preselectedReaderIds || Array.from(selectedReaderIds);
    if (ids.length === 0) { alert('Select at least one reader first'); return; }
    setSelectedReaderIds(new Set(ids));

    // Default to first book
    const defaultBook = books[0];
    const bookTitle = defaultBook?.title || 'your book';
    setArcBookId(defaultBook?.id || '');
    setArcSubject(`Your ARC copy of ${bookTitle} is here!`);
    setArcBody(
      `Hi {readerFirstName},\n\nThank you so much for signing up to be an ARC reader! Your advance copy of "${bookTitle}" is attached to this email.\n\nPlease read at your own pace and, if you enjoy it, consider leaving a review on Amazon, Goodreads, or wherever you prefer. Every review makes a huge difference for an indie author.\n\nHappy reading!\n\nWarm regards`
    );
    setArcEpubFile(null);
    setArcUploadProgress('');
    setShowSendArcModal(true);
  }

  function handleArcBookChange(bookId) {
    setArcBookId(bookId);
    const book = books.find(b => b.id === bookId);
    const title = book?.title || 'your book';
    setArcSubject(`Your ARC copy of ${title} is here!`);
    setArcBody(prev => prev.replace(/"[^"]*"/, `"${title}"`));
  }

  async function handleSendArc() {
    const allReaders = Object.values(perBookReaders).flat();
    const selectedList = allReaders.filter(r => selectedReaderIds.has(r.id));
    if (selectedList.length === 0) { alert('No readers selected'); return; }
    if (!arcSubject.trim() || !arcBody.trim()) { alert('Subject and body are required'); return; }

    // Split into Kindle-eligible and skipped
    const withKindle = selectedList.filter(r =>
      getKindleEmail(r, globalKindleMap).toLowerCase().endsWith('@kindle.com')
    );
    const skippedList = selectedList.filter(r =>
      !getKindleEmail(r, globalKindleMap).toLowerCase().endsWith('@kindle.com')
    );

    if (withKindle.length === 0) {
      // All selected readers missing Kindle — show the dialog with no proceed action
      setKindleMissingPrompt({
        skippedNames: skippedList.map(r => r.name || r.emailCorrespondence).filter(Boolean),
        onProceed: null,
      });
      return;
    }
    if (skippedList.length > 0) {
      // Mixed — show the dialog with proceed = continue without missing-Kindle readers
      setKindleMissingPrompt({
        skippedNames: skippedList.map(r => r.name || r.emailCorrespondence).filter(Boolean),
        onProceed: () => {
          setKindleMissingPrompt(null);
          performSendArc(withKindle, skippedList);
        },
      });
      return;
    }

    performSendArc(withKindle, skippedList);
  }

  async function performSendArc(withKindle, skippedList) {
    setIsSendingArc(true);
    setArcSendProgress({ sent: 0, failed: 0, skipped: skippedList.length, total: withKindle.length, current: 0 });

    try {
      // Upload ePUB to Firebase Storage if provided
      let attachmentUrl = '';
      let attachmentName = '';
      if (arcEpubFile) {
        setArcUploadProgress('Uploading ePUB...');
        const authorId = user.uid;
        const fileRef = storageRef(storage, `authors/${authorId}/arcs/${Date.now()}-${arcEpubFile.name}`);
        await uploadBytes(fileRef, arcEpubFile);
        attachmentUrl = await getDownloadURL(fileRef);
        attachmentName = arcEpubFile.name;
      }
      setArcUploadProgress('');

      // Send in batches of 10 for real-time progress
      const BATCH_SIZE = 10;
      const bookTitle = books.find(b => b.id === arcBookId)?.title || '';
      let totalSent = 0;
      let totalFailed = 0;
      const allFailed = [];
      const sentReaderIds = new Set();

      for (let i = 0; i < withKindle.length; i += BATCH_SIZE) {
        const batch = withKindle.slice(i, i + BATCH_SIZE);
        const recipients = batch.map(r => ({
          readerId: r.id,
          email: getKindleEmail(r, globalKindleMap),
          name: r.name || 'Reader',
        }));

        setArcSendProgress(prev => ({ ...prev, current: i + 1 }));

        try {
          const sendArcEmails = httpsCallable(functions, 'sendArcEmails');
          const result = await sendArcEmails({
            recipients,
            subject: arcSubject,
            body: arcBody,
            bookId: arcBookId,
            bookTitle,
            attachmentUrl,
            attachmentName,
          });

          const { sentCount, failedCount, failed } = result.data;
          totalSent += sentCount;
          totalFailed += failedCount;
          if (failed?.length) allFailed.push(...failed);

          // Track which readers were successfully sent
          const failedEmails = new Set((failed || []).map(f => f.email?.toLowerCase()));
          batch.forEach(r => {
            if (!failedEmails.has(getKindleEmail(r, globalKindleMap).toLowerCase())) {
              sentReaderIds.add(r.id);
            }
          });
        } catch (batchErr) {
          console.error('Batch send error:', batchErr);
          totalFailed += batch.length;
          allFailed.push(...batch.map(r => ({ email: getKindleEmail(r, globalKindleMap), error: batchErr.message })));
        }

        setArcSendProgress(prev => ({
          ...prev,
          sent: totalSent,
          failed: totalFailed,
          current: Math.min(i + BATCH_SIZE, withKindle.length),
        }));
      }

      // Update per-book reader status for successfully sent ones
      for (const r of withKindle) {
        if (sentReaderIds.has(r.id)) {
          const bkId = r._bookId || arcBookId;
          if (bkId) {
            try {
              await updateDoc(doc(db, basePath, 'books', bkId, 'readers', r.id), {
                status: 'ARC sent',
                arcSent: new Date().toISOString().split('T')[0],
                lastModified: serverTimestamp(),
              });
            } catch { /* skip */ }
          }
        }
      }

      setShowSendArcModal(false);
      setSelectedReaderIds(new Set());
      setArcEpubFile(null);
      setArcUploadProgress('');
      setArcSendProgress(null);

      // Results summary
      const parts = [`${totalSent} sent`];
      if (skippedList.length > 0) parts.push(`${skippedList.length} skipped (no Kindle email)`);
      if (totalFailed > 0) parts.push(`${totalFailed} failed`);
      const summary = parts.join(', ');

      if (totalFailed > 0) {
        alert(`ARC Send Complete: ${summary}\n\nFailed:\n${allFailed.map(f => `${f.email}: ${f.error}`).join('\n')}`);
      } else {
        alert(`ARC Send Complete: ${summary}`);
      }
    } catch (error) {
      console.error('Send ARC error:', error);
      alert('Error sending ARC emails: ' + (error.message || 'Unknown error'));
    }

    setIsSendingArc(false);
    setArcSendProgress(null);
  }

  function toggleReaderSelection(readerId) {
    setSelectedReaderIds(prev => {
      const next = new Set(prev);
      if (next.has(readerId)) next.delete(readerId);
      else next.add(readerId);
      return next;
    });
  }

  function toggleAllReadersInBook(bookReaders) {
    const allSelected = bookReaders.every(r => selectedReaderIds.has(r.id));
    setSelectedReaderIds(prev => {
      const next = new Set(prev);
      if (allSelected) {
        bookReaders.forEach(r => next.delete(r.id));
      } else {
        bookReaders.forEach(r => next.add(r.id));
      }
      return next;
    });
  }

  /* ─── Quick Add from Email ──── */
  async function handleQuickAddFromEmail() {
    if (!pastedEmailText.trim()) { alert('Please paste the email content first'); return; }
    setIsParsingEmail(true);
    try {
      const parsed = await parseReaderFromEmail(pastedEmailText);
      if (!parsed.emailCorrespondence) {
        alert('Could not find an email address in the pasted text. Please check the content and try again.');
        return;
      }
      const targetBookId = filterBook !== 'All' ? filterBook : books[0]?.id;
      if (!targetBookId) { alert('No book selected. Please filter by a book first.'); setIsParsingEmail(false); return; }
      const readerData = {
        name: parsed.name || '',
        emailCorrespondence: parsed.emailCorrespondence,
        eReaderEmail: parsed.eReaderEmail || parsed.emailCorrespondence,
        acceptsMarketing: parsed.acceptsMarketing !== false,
        interestedIn: parsed.interestedIn || 'eBook ARC request',
        reviewPlatforms: Array.isArray(parsed.reviewPlatforms) ? parsed.reviewPlatforms : [],
        socialUsernames: parsed.socialUsernames || '',
        favoriteBooks: parsed.favoriteBooks || '',
        location: parsed.location || '',
        signupDate: new Date().toISOString().split('T')[0],
        status: 'Noted ARC interest',
        arcSent: '', reviewDate: '', rating: '', notes: '',
        totalPoints: 0, totalReviews: 0,
        authorId: user.uid,
        bookId: targetBookId,
        masterReaderId: parsed.emailCorrespondence,
        createdAt: serverTimestamp(),
        lastModified: serverTimestamp(),
      };
      await setDoc(
        doc(db, basePath, 'books', targetBookId, 'readers', readerData.emailCorrespondence),
        { ...readerData, source: 'manual' },
        { merge: true }
      );
      alert(`${readerData.name || readerData.emailCorrespondence} added to ${books.find(b => b.id === targetBookId)?.title || 'book'}!`);
      setShowQuickAddModal(false);
      setPastedEmailText('');
    } catch (error) {
      alert('Error adding reader: ' + error.message);
    }
    setIsParsingEmail(false);
  }

  /* ─── Export CSV ──── */
  function exportToCSV() {
    const allReaders = Object.values(perBookReaders).flat();
    if (allReaders.length === 0) { alert('No readers to export'); return; }
    const headers = [
      'Name', 'Email (Correspondence)', 'Email (eReader)', 'Book', 'Status', 'Platforms',
      'Primary Social', 'Follower Count', 'Location', 'Signup Date', 'ARC Sent Date',
      'Review Date', 'Rating', 'Total Points', 'Total Reviews', 'Notes',
    ];
    const rows = allReaders.map(reader => {
      const platforms = Array.isArray(reader.reviewPlatforms) ? reader.reviewPlatforms.join('; ') : '';
      const bookTitle = books.find(b => b.id === reader._bookId)?.title || reader._bookId || '';
      return [
        reader.name || '', reader.emailCorrespondence || '', getKindleEmail(reader, globalKindleMap) || '',
        bookTitle, getDisplayStatus(reader), platforms, reader.primaryPlatform || '', reader.followerCount || 0,
        reader.location || '', reader.signupDate || '', reader.arcSent || '',
        reader.reviewDate || '', reader.rating || '', reader.totalPoints || 0,
        reader.totalReviews || 0, (reader.notes || '').replace(/"/g, '""'),
      ];
    });
    const csvContent = [
      headers.map(h => `"${h}"`).join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `arc-readers-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /* ─── Backfill Ratings ──── */
  async function handleBackfillRatings() {
    if (!window.confirm('This will copy star ratings from all approved reviews onto per-book reader records so filters work. Continue?')) return;
    const rated = reviews.filter(r => r.status === 'approved' && r.rating);
    if (rated.length === 0) { alert('No approved reviews with ratings found.'); return; }
    let updated = 0;
    const allReaders = Object.values(perBookReaders).flat();
    for (const rev of rated) {
      const matchingReaders = allReaders.filter(r => r.emailCorrespondence === rev.readerEmail || r.id === rev.readerId);
      for (const reader of matchingReaders) {
        if (reader._bookId) {
          try {
            await updateDoc(doc(db, basePath, 'books', reader._bookId, 'readers', reader.id), { reviewRating: rev.rating });
            updated++;
          } catch { /* skip */ }
        }
      }
    }
    alert(`Updated ${updated} reader records with star ratings.`);
  }

  /* ========================================
     RENDER
  ======================================== */
  return (
    <>
      {/* STATS */}
      <section className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total Readers" value={stats.totalReaders} icon={Mail} accent="bg-slate-500/15 text-teal-700" />
        <StatCard label="ARCs Sent" value={stats.arcsSent} icon={BookOpen} accent="bg-sky-500/15 text-sky-700" />
        <StatCard label="Reviewed" value={stats.reviewed} icon={Star} accent="bg-emerald-500/15 text-emerald-700" />
        <StatCard label="Average Rating" value={stats.avgRating} icon={Star} accent="bg-violet-500/15 text-violet-700" />
      </section>

      {/* READERS TOOLBAR */}
      <section className="mb-4 rounded-2xl border border-slate-100 bg-white p-3 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          {/* SEARCH */}
          <div className="flex flex-1 items-center gap-3">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, email, username or platform…"
                className="w-full rounded-lg border border-slate-200 bg-white px-7 py-1.5 text-[11px] text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
              />
            </div>

            {/* FILTERS */}
            <div className="flex items-center gap-2">
              <Filter className="h-3.5 w-3.5 text-slate-400" />
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-slate-800 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200">
                <option value="All">All statuses</option>
                <option value="Noted ARC interest">Noted ARC interest</option>
                <option value="Engaged">Engaged</option>
                <option value="Need follow up">Need follow up</option>
                <option value="Accepted">Accepted</option>
                <option value="ARC sent">ARC sent</option>
                <option value="Reviewed">Reviewed</option>
                <option value="Closed">Closed</option>
                <option value="Street Team">Street Team</option>
                <option value="Book 2 ARC interest">Book 2 ARC interest</option>
                <option value="New Buyer">New Buyer</option>
              </select>

              {/* SOURCE FILTER */}
              <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-slate-800 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200">
                <option value="All">All sources</option>
                <option value="reader_portal">Portal only</option>
                <option value="manual">Manual only</option>
              </select>

              {/* ARC FILTER */}
              <select value={filterArc} onChange={(e) => setFilterArc(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-slate-800 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200">
                <option value="All">All ARCs</option>
                <option value="Has ARC">Has approved ARC</option>
                <option value="No ARC">No ARC</option>
              </select>

              {/* EREADER EMAIL FILTER */}
              <select value={filterEReader} onChange={(e) => setFilterEReader(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-slate-800 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200">
                <option value="All">All Kindle emails</option>
                <option value="Has Email">✅ Has Kindle email</option>
                <option value="Missing">❌ Missing</option>
                <option value="Duplicate">⚠️ Needs check</option>
              </select>

              {/* STAR RATING FILTER */}
              <select value={filterRating} onChange={(e) => setFilterRating(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-slate-800 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200">
                <option value="All">All ratings</option>
                <option value="5">⭐⭐⭐⭐⭐ 5 stars</option>
                <option value="4">⭐⭐⭐⭐ 4 stars</option>
                <option value="3">⭐⭐⭐ 3 stars</option>
                <option value="unrated">No rating yet</option>
              </select>

              {/* BOOK FILTER */}
              <select value={filterBook} onChange={(e) => setFilterBook(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-slate-800 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200">
                <option value="All">All books</option>
                {books.map((book) => (
                  <option key={book.id} value={book.id}>{book.title}</option>
                ))}
              </select>
            </div>
          </div>

          {/* ACTION BUTTONS */}
          <div className="flex flex-wrap gap-2">
            {/* Select All Visible */}
            {(() => {
              const allVisible = books
                .filter(b => filterBook === 'All' || b.id === filterBook)
                .flatMap(b => applyReaderFilters(perBookReaders[b.id] || []));
              const allSelected = allVisible.length > 0 && allVisible.every(r => selectedReaderIds.has(r.id));
              return allVisible.length > 0 && (
                <button
                  onClick={() => {
                    setSelectedReaderIds(prev => {
                      const next = new Set(prev);
                      if (allSelected) {
                        allVisible.forEach(r => next.delete(r.id));
                      } else {
                        allVisible.forEach(r => next.add(r.id));
                      }
                      return next;
                    });
                  }}
                  className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
                >
                  <CheckSquare className="h-3.5 w-3.5" />
                  {allSelected ? 'Deselect All' : `Select All (${allVisible.length})`}
                </button>
              );
            })()}
            {selectedReaderIds.size > 0 && (
              <button onClick={() => openSendArcModal()}
                className="flex items-center gap-1 rounded-lg bg-teal-600 px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:bg-teal-700 animate-pulse">
                <Send className="h-3.5 w-3.5" /> Send ARC ({selectedReaderIds.size})
              </button>
            )}
            <button onClick={() => openNewReaderDrawer()}
              className="flex items-center gap-1 rounded-lg bg-teal-600 px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:bg-teal-700">
              <Plus className="h-3.5 w-3.5" /> Add Reader
            </button>
            <button onClick={() => setShowQuickAddModal(true)}
              className="flex items-center gap-1 rounded-lg border border-purple-200 bg-purple-50 px-3 py-1.5 text-[11px] font-medium text-purple-800 hover:bg-purple-100">
              <Mail className="h-3.5 w-3.5" /> Quick Add
            </button>
            <button onClick={exportToCSV}
              className="flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[11px] font-medium text-emerald-800 hover:bg-emerald-100">
              <Download className="h-3.5 w-3.5" /> CSV
            </button>
            <button onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-[11px] font-medium text-blue-800 hover:bg-blue-100">
              <Upload className="h-3.5 w-3.5" /> Upload
            </button>
            <input ref={fileInputRef} type="file" accept=".csv" onChange={() => {}} className="hidden" />
          </div>
        </div>
      </section>

      {/* GROUPED BY BOOK */}
      <section className="space-y-3">
        {/* RENDER EACH BOOK */}
        {books.filter(b => filterBook === 'All' || b.id === filterBook).map((book) => {
          const bookReaders = applyReaderFilters(perBookReaders[book.id] || []);

          if (bookReaders.length === 0 && searchTerm === '' && filterStatus === 'All' && filterBook === 'All') {
            return null;
          }

          const isCollapsed = collapsedBooks[book.id];
          const reviewedCount = bookReaders.filter(r => getDisplayStatus(r) === 'Reviewed').length;

          return (
            <div key={book.id} className="rounded-2xl border border-slate-100 bg-white shadow-sm overflow-hidden">
              {/* BOOK HEADER */}
              <button
                onClick={() => setCollapsedBooks({ ...collapsedBooks, [book.id]: !isCollapsed })}
                className="w-full bg-gradient-to-r from-slate-50 to-slate-100/60 px-4 py-3 text-left hover:from-slate-100 hover:to-slate-200/60 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <BookOpen className="h-4 w-4 text-teal-600" />
                    <div>
                      <h3 className="text-[13px] font-bold text-slate-900">{book.title}</h3>
                      {book.series && <p className="text-[10px] text-slate-500">{book.series}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-[10px] text-slate-500">Readers</p>
                      <p className="text-[13px] font-bold text-slate-900">{bookReaders.length}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] text-slate-500">Reviewed</p>
                      <p className="text-[13px] font-bold text-emerald-600">{reviewedCount}</p>
                    </div>
                    <div className="text-slate-400">{isCollapsed ? '▼' : '▲'}</div>
                  </div>
                </div>
              </button>

              {/* READERS TABLE */}
              {!isCollapsed && (
                <div className="overflow-x-auto">
                  {bookReaders.length === 0 ? (
                    <div className="px-4 py-6 text-center text-[11px] text-slate-500">
                      No readers assigned to this book yet.
                    </div>
                  ) : (
                    <table className="min-w-full text-[11px]">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-2 py-2 text-center w-8">
                            <input type="checkbox"
                              checked={bookReaders.length > 0 && bookReaders.every(r => selectedReaderIds.has(r.id))}
                              onChange={() => toggleAllReadersInBook(bookReaders)}
                              className="h-3.5 w-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                            />
                          </th>
                          <th className="px-3 py-2 text-left">Reader</th>
                          <th className="px-3 py-2 text-left">Kindle Email</th>
                          <th className="px-3 py-2 text-left">Status</th>
                          <th className="px-3 py-2 text-left">Rating</th>
                          <th className="px-3 py-2 text-center">ARC</th>
                          <th className="px-3 py-2 text-left">Points</th>
                          <th className="px-3 py-2 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bookReaders.map((r) => (
                          <tr key={r.id} className={`border-t border-slate-100 hover:bg-slate-50/60 ${selectedReaderIds.has(r.id) ? 'bg-teal-50/40' : ''}`}>
                            <td className="px-2 py-2 align-top text-center">
                              <input type="checkbox"
                                checked={selectedReaderIds.has(r.id)}
                                onChange={() => toggleReaderSelection(r.id)}
                                className="h-3.5 w-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                              />
                            </td>
                            <td
                              className="px-3 py-2 align-top cursor-pointer hover:bg-slate-50/50 transition-colors"
                              onClick={() => openReaderDetailPanel(r)}
                            >
                              <div className="flex items-center gap-2">
                                <div>
                                  <p className="font-medium text-teal-700 hover:text-slate-900">{r.name || 'Unnamed'}</p>
                                  <p className="text-[10px] text-slate-500">{r.emailCorrespondence}</p>
                                  {r.location && <p className="text-[10px] text-slate-400">{r.location}</p>}
                                </div>
                                <SourceBadge source={r.source} />
                              </div>
                            </td>
                            {(() => {
                              const kindle = getKindleEmail(r, globalKindleMap);
                              return (
                                <td className="px-3 py-2 align-top">
                                  <div className="flex items-center gap-1.5">
                                    <EReaderEmailIcon emailCorrespondence={r.emailCorrespondence} eReaderEmail={kindle} />
                                    <span className="text-[10px] truncate max-w-[120px]">
                                      {!kindle
                                        ? <span className="text-red-500 font-medium">No Kindle email</span>
                                        : kindle.toLowerCase().endsWith('@kindle.com')
                                          ? <span className="text-slate-600">{kindle}</span>
                                          : <span className="text-orange-600">{kindle} <span className="italic">(not Kindle)</span></span>
                                      }
                                    </span>
                                  </div>
                                </td>
                              );
                            })()}
                            <td className="px-3 py-2 align-top">
                              <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${getStatusColor(getDisplayStatus(r))}`}>
                                {getDisplayStatus(r)}
                              </span>
                            </td>
                            <td className="px-3 py-2 align-top">
                              {r.reviewRating ? (
                                <div className="flex items-center gap-1">
                                  <span className="flex">
                                    {[1,2,3,4,5].map(star => (
                                      <Star key={star} className={`h-3 w-3 ${Number(r.reviewRating) >= star ? 'text-teal-400 fill-teal-400' : 'text-slate-200 fill-slate-200'}`} />
                                    ))}
                                  </span>
                                  <span className="text-[10px] font-semibold text-slate-700">{r.reviewRating}</span>
                                </div>
                              ) : (
                                <span className="text-[10px] text-slate-400 italic">No rating</span>
                              )}
                            </td>
                            <td className="px-3 py-2 align-top text-center">
                              {r.hasApprovedArcRequest ? (
                                <span className="inline-flex items-center justify-center rounded-full bg-emerald-100 p-1" title="ARC Approved">
                                  <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />
                                </span>
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2 align-top">{r.totalPoints || 0}</td>
                            <td className="px-3 py-2 align-top text-right">
                              <div className="inline-flex gap-1">
                                <button onClick={(e) => { e.stopPropagation(); openSendArcModal([r.id]); }}
                                  className="rounded-full border border-teal-200 bg-teal-50 p-1 text-teal-600 hover:bg-teal-100" title="Send ARC email">
                                  <Send className="h-3.5 w-3.5" />
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); const amount = prompt('How many points to add?', '10'); const reason = prompt('Reason for adding points?', 'Quick bonus'); if (amount && reason) handleAddPoints(r.id, Number(amount), reason, book.id); }}
                                  className="rounded-full border border-green-200 bg-green-50 p-1 text-green-600 hover:bg-green-100" title="Quick add points">
                                  <Plus className="h-3.5 w-3.5" />
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); const amount = prompt('How many points to deduct?', '10'); const reason = prompt('Reason for deducting points?', 'Penalty'); if (amount && reason) handleDeductPoints(r.id, Number(amount), reason, book.id); }}
                                  className="rounded-full border border-red-200 bg-red-50 p-1 text-red-600 hover:bg-red-100" title="Quick deduct points">
                                  <Minus className="h-3.5 w-3.5" />
                                </button>
                                <button onClick={() => openEditReaderDrawer(r, book.id)}
                                  className="rounded-full border border-slate-200 bg-white p-1 text-slate-600 hover:text-teal-700">
                                  <Edit2 className="h-3.5 w-3.5" />
                                </button>
                                <button onClick={() => handleDeleteReader(r.id, book.id)}
                                  className="rounded-full border border-rose-200 bg-rose-50 p-1 text-rose-600 hover:bg-rose-100">
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* WAITLISTED READERS for this book */}
              {!isCollapsed && (() => {
                const apps = bookApplications[book.id] || [];
                const waitlistedApps = apps.filter(a =>
                  a.status === 'waitlisted' || a.status === 'pending'
                );
                if (waitlistedApps.length === 0) return null;
                const isWaitlistCollapsed = collapsedBooks[`${book.id}-waitlist`];

                return (
                  <div className="border-t border-dashed border-slate-200">
                    <button
                      onClick={() => setCollapsedBooks({ ...collapsedBooks, [`${book.id}-waitlist`]: !isWaitlistCollapsed })}
                      className="w-full bg-gradient-to-r from-yellow-50/80 to-yellow-100/40 px-4 py-2 text-left hover:from-yellow-100/80 hover:to-yellow-200/40 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Clock className="h-3.5 w-3.5 text-yellow-600" />
                          <p className="text-[11px] font-semibold text-yellow-800">Waitlisted &amp; Pending</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <p className="text-[11px] font-bold text-yellow-700">{waitlistedApps.length}</p>
                          <div className="text-slate-400 text-[10px]">{isWaitlistCollapsed ? '▼' : '▲'}</div>
                        </div>
                      </div>
                    </button>

                    {!isWaitlistCollapsed && (
                      <div className="overflow-x-auto bg-yellow-50/30">
                        <table className="min-w-full text-[11px]">
                          <thead className="bg-yellow-50/60">
                            <tr>
                              <th className="px-3 py-2 text-left">Reader</th>
                              <th className="px-3 py-2 text-left">Email</th>
                              <th className="px-3 py-2 text-left">Application Status</th>
                              <th className="px-3 py-2 text-left">Applied</th>
                              <th className="px-3 py-2 text-left">Message</th>
                            </tr>
                          </thead>
                          <tbody>
                            {waitlistedApps.map((app) => (
                              <tr key={app.id} className="border-t border-yellow-100/60 hover:bg-yellow-50/50">
                                <td className="px-3 py-2 align-top">
                                  <p className="font-medium text-slate-800">{app.readerName || 'Unknown'}</p>
                                </td>
                                <td className="px-3 py-2 align-top">
                                  <p className="text-[10px] text-slate-500">{app.readerEmail || ''}</p>
                                </td>
                                <td className="px-3 py-2 align-top">
                                  <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${
                                    app.status === 'waitlisted' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'
                                  }`}>
                                    {app.status === 'waitlisted' ? 'Waitlisted' : 'Pending'}
                                  </span>
                                </td>
                                <td className="px-3 py-2 align-top text-[10px] text-slate-500">
                                  {app.appliedAt?.toDate ? app.appliedAt.toDate().toLocaleDateString('en-GB') : ''}
                                </td>
                                <td className="px-3 py-2 align-top text-[10px] text-slate-500 max-w-[200px]">
                                  {app.message ? (() => {
                                    const isExpanded = expandedAppMessages.has(app.id);
                                    const isLong = app.message.length > 150;
                                    const display = isLong && !isExpanded ? `${app.message.slice(0, 150).trimEnd()}…` : app.message;
                                    return (
                                      <>
                                        <span>{display}</span>
                                        {isLong && (
                                          <>
                                            {' '}
                                            <button
                                              type="button"
                                              aria-expanded={isExpanded}
                                              onClick={() => {
                                                setExpandedAppMessages(prev => {
                                                  const next = new Set(prev);
                                                  if (next.has(app.id)) next.delete(app.id);
                                                  else next.add(app.id);
                                                  return next;
                                                });
                                              }}
                                              className="font-medium text-teal-700 hover:text-slate-900"
                                            >
                                              {isExpanded ? 'Show less' : 'Show more'}
                                            </button>
                                          </>
                                        )}
                                      </>
                                    );
                                  })() : <span className="italic text-slate-400">No message</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          );
        })}

        {/* UNASSIGNED READERS */}
        {(() => {
          if (filterBook !== 'All') return null;
          // Show global readers that don't have any per-book records
          const allPerBookEmails = new Set(
            Object.values(perBookReaders).flat().map(r => r.emailCorrespondence?.toLowerCase()).filter(Boolean)
          );
          const allPerBookIds = new Set(
            Object.values(perBookReaders).flat().map(r => r.masterReaderId || r.id).filter(Boolean)
          );
          const unassignedReaders = applyReaderFilters(readers.filter(r =>
            !allPerBookEmails.has(r.emailCorrespondence?.toLowerCase()) &&
            !allPerBookIds.has(r.id)
          ));
          if (unassignedReaders.length === 0) return null;
          const isCollapsed = collapsedBooks['unassigned'];

          return (
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <button
                onClick={() => setCollapsedBooks({ ...collapsedBooks, 'unassigned': !isCollapsed })}
                className="w-full bg-gradient-to-r from-slate-50 to-slate-100/60 px-4 py-3 text-left hover:from-slate-100 hover:to-slate-200/60 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-slate-500" />
                    <div>
                      <h3 className="text-[13px] font-bold text-slate-900">Unassigned Readers</h3>
                      <p className="text-[10px] text-slate-500">Not assigned to any book yet</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-[10px] text-slate-500">Readers</p>
                      <p className="text-[13px] font-bold text-slate-900">{unassignedReaders.length}</p>
                    </div>
                    <div className="text-slate-400">{isCollapsed ? '▼' : '▲'}</div>
                  </div>
                </div>
              </button>

              {!isCollapsed && (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-[11px]">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Reader</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">Rating</th>
                        <th className="px-3 py-2 text-left">Points</th>
                        <th className="px-3 py-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unassignedReaders.map((r) => (
                        <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                          <td className="px-3 py-2 align-top">
                            <p className="font-medium">{r.name || 'Unnamed'}</p>
                            <p className="text-[10px] text-slate-500">{r.emailCorrespondence}</p>
                            {r.location && <p className="text-[10px] text-slate-400">{r.location}</p>}
                          </td>
                          <td className="px-3 py-2 align-top">
                            <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${getStatusColor(getDisplayStatus(r))}`}>
                              {getDisplayStatus(r)}
                            </span>
                          </td>
                          <td className="px-3 py-2 align-top">
                            {r.reviewRating ? (
                              <div className="flex items-center gap-1">
                                <span className="flex">
                                  {[1,2,3,4,5].map(star => (
                                    <Star key={star} className={`h-3 w-3 ${Number(r.reviewRating) >= star ? 'text-teal-400 fill-teal-400' : 'text-slate-200 fill-slate-200'}`} />
                                  ))}
                                </span>
                                <span className="text-[10px] font-semibold text-slate-700">{r.reviewRating}</span>
                              </div>
                            ) : (
                              <span className="text-[10px] text-slate-400 italic">No rating</span>
                            )}
                          </td>
                          <td className="px-3 py-2 align-top">{r.totalPoints || 0}</td>
                          <td className="px-3 py-2 align-top text-right">
                            <div className="inline-flex gap-1">
                              <button onClick={(e) => { e.stopPropagation(); const amount = prompt('How many points to add?', '10'); const reason = prompt('Reason for adding points?', 'Quick bonus'); if (amount && reason) handleAddPoints(r.id, Number(amount), reason); }}
                                className="rounded-full border border-green-200 bg-green-50 p-1 text-green-600 hover:bg-green-100" title="Quick add points">
                                <Plus className="h-3.5 w-3.5" />
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); const amount = prompt('How many points to deduct?', '10'); const reason = prompt('Reason for deducting points?', 'Penalty'); if (amount && reason) handleDeductPoints(r.id, Number(amount), reason); }}
                                className="rounded-full border border-red-200 bg-red-50 p-1 text-red-600 hover:bg-red-100" title="Quick deduct points">
                                <Minus className="h-3.5 w-3.5" />
                              </button>
                              <button onClick={() => openEditReaderDrawer(r)}
                                className="rounded-full border border-slate-200 bg-white p-1 text-slate-600 hover:text-teal-700">
                                <Edit2 className="h-3.5 w-3.5" />
                              </button>
                              <button onClick={() => handleDeleteReader(r.id)}
                                className="rounded-full border border-rose-200 bg-rose-50 p-1 text-rose-600 hover:bg-rose-100">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })()}

        {/* NO RESULTS MESSAGE */}
        {filteredReaders.length === 0 && (
          <div className="rounded-2xl border border-slate-100 bg-white p-12 text-center text-[11px] text-slate-500">
            No readers found matching your search.
          </div>
        )}
      </section>

      {/* ----------------------------------------
           READER DETAIL PANEL
      ---------------------------------------- */}
      {showReaderDetailPanel && viewingReader && (
        <div className="fixed inset-0 z-50 flex items-center justify-end bg-slate-900/30 backdrop-blur-sm">
          <div className="h-full w-full max-w-2xl overflow-y-auto border-l border-slate-100 bg-white shadow-2xl">

            {/* Header */}
            <div className="sticky top-0 z-10 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-bold text-slate-900">{viewingReader.name || 'Unnamed Reader'}</h2>
                    <SourceBadge source={viewingReader.source} />
                  </div>
                  <p className="text-sm text-slate-500">{viewingReader.emailCorrespondence}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={switchToEditFromDetail}
                    className="flex items-center gap-2 rounded-lg border border-teal-300 bg-slate-50 px-4 py-2 text-sm font-medium text-teal-700 hover:bg-slate-100">
                    <Edit2 className="h-4 w-4" /> Edit
                  </button>
                  <button onClick={closeReaderDetailPanel}
                    className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
                    Close
                  </button>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">

              {/* Stats Cards */}
              <div className="grid grid-cols-3 gap-4">
                <div className="rounded-xl border border-slate-100 bg-gradient-to-br from-white via-slate-50/60 to-slate-100/40 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-slate-500">Total Reviews</p>
                    <Star className="h-5 w-5 text-teal-600" />
                  </div>
                  <p className="text-3xl font-bold text-slate-900">{readerReviews.length}</p>
                </div>
                <div className="rounded-xl border border-slate-100 bg-gradient-to-br from-white via-slate-50/60 to-slate-100/40 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-slate-500">Total Points</p>
                    <Gift className="h-5 w-5 text-teal-600" />
                  </div>
                  <p className="text-3xl font-bold text-slate-900">{viewingReader.totalPoints || 0}</p>
                </div>
                <div className="rounded-xl border border-slate-100 bg-gradient-to-br from-white via-slate-50/60 to-slate-100/40 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-slate-500">Books Received</p>
                    <BookOpen className="h-5 w-5 text-teal-600" />
                  </div>
                  <p className="text-3xl font-bold text-slate-900">{(viewingReader.booksReceived || []).length}</p>
                </div>
              </div>

              {/* Basic Information */}
              <div className="rounded-xl border border-slate-100 bg-white p-5">
                <h3 className="mb-4 text-lg font-bold text-slate-900">📋 Basic Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="mb-1 text-xs font-medium text-slate-500">Name</p>
                    <p className="text-slate-900">{viewingReader.name || '—'}</p>
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-medium text-slate-500">Status</p>
                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${getStatusColor(getDisplayStatus(viewingReader))}`}>
                      {getDisplayStatus(viewingReader)}
                    </span>
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-medium text-slate-500">Email (correspondence)</p>
                    <p className="text-slate-900">{viewingReader.emailCorrespondence || '—'}</p>
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-medium text-slate-500">eReader Email</p>
                    <div className="flex items-center gap-2">
                      <p className="text-slate-900">{getKindleEmail(viewingReader, globalKindleMap) || '—'}</p>
                      <EReaderEmailIcon emailCorrespondence={viewingReader.emailCorrespondence} eReaderEmail={getKindleEmail(viewingReader, globalKindleMap)} />
                    </div>
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-medium text-slate-500">First Name</p>
                    <p className="text-slate-900">{viewingReader.firstName || '—'}</p>
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-medium text-slate-500">Last Name</p>
                    <p className="text-slate-900">{viewingReader.lastName || '—'}</p>
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-medium text-slate-500">Location</p>
                    <p className="text-slate-900">{viewingReader.location || '—'}</p>
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-medium text-slate-500">Signup Date</p>
                    <p className="text-slate-900">{viewingReader.signupDate || '—'}</p>
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              {viewingReader.shippingAddress && (
                <div className="rounded-xl border border-slate-100 bg-white p-5">
                  <h3 className="mb-4 text-lg font-bold text-slate-900">📦 Shipping Address</h3>
                  <div className="rounded-lg bg-slate-50 p-4">
                    <p className="text-sm text-slate-900 leading-relaxed">
                      {viewingReader.shippingAddress.line1 && <>{viewingReader.shippingAddress.line1}<br /></>}
                      {viewingReader.shippingAddress.line2 && <>{viewingReader.shippingAddress.line2}<br /></>}
                      {viewingReader.shippingAddress.city && <>{viewingReader.shippingAddress.city} </>}
                      {viewingReader.shippingAddress.postalCode && <>{viewingReader.shippingAddress.postalCode}<br /></>}
                      {viewingReader.shippingAddress.country && <>{viewingReader.shippingAddress.country}</>}
                      {!viewingReader.shippingAddress.line1 && <span className="text-slate-400">No address provided</span>}
                    </p>
                  </div>
                </div>
              )}

              {/* Social Media & Review Platforms */}
              <div className="rounded-xl border border-slate-100 bg-white p-5">
                <h3 className="mb-4 text-lg font-bold text-slate-900">📱 Social Media & Review Platforms</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="mb-1 text-xs font-medium text-slate-500">📷 Instagram</p>
                    {viewingReader.socialMedia?.instagram ? (
                      <a href={`https://instagram.com/${viewingReader.socialMedia.instagram.replace('@', '')}`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-teal-600 hover:text-slate-800 flex items-center gap-1">
                        {viewingReader.socialMedia.instagram}
                        <ExternalLink size={12} />
                      </a>
                    ) : (<p className="text-slate-400">—</p>)}
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-medium text-slate-500">📱 TikTok</p>
                    {viewingReader.socialMedia?.tiktok ? (
                      <a href={`https://tiktok.com/@${viewingReader.socialMedia.tiktok.replace('@', '')}`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-teal-600 hover:text-slate-800 flex items-center gap-1">
                        {viewingReader.socialMedia.tiktok}
                        <ExternalLink size={12} />
                      </a>
                    ) : (<p className="text-slate-400">—</p>)}
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-medium text-slate-500">📚 Goodreads</p>
                    <p className="text-slate-900">{viewingReader.socialMedia?.goodreads || '—'}</p>
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-medium text-slate-500">📦 Amazon</p>
                    <p className="text-slate-900">{viewingReader.socialMedia?.amazon || '—'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="mb-1 text-xs font-medium text-slate-500">📝 Other Sites</p>
                    <p className="text-slate-900">{viewingReader.socialMedia?.other || '—'}</p>
                  </div>
                </div>
              </div>

              {/* Reading Preferences */}
              {viewingReader.preferences && (
                <div className="rounded-xl border border-slate-100 bg-white p-5">
                  <h3 className="mb-4 text-lg font-bold text-slate-900">📚 Reading Preferences</h3>
                  <div className="space-y-3 text-sm">
                    <div>
                      <p className="mb-2 text-xs font-medium text-slate-500">Favorite Genres</p>
                      <div className="flex flex-wrap gap-2">
                        {viewingReader.preferences.favoriteGenres?.length > 0 ? (
                          viewingReader.preferences.favoriteGenres.map(genre => (
                            <span key={genre} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-teal-700">{genre}</span>
                          ))
                        ) : (<p className="text-slate-400">No genres selected</p>)}
                      </div>
                    </div>
                    <div>
                      <p className="mb-1 text-xs font-medium text-slate-500">Favorite Tropes</p>
                      <p className="text-slate-900">{viewingReader.preferences.favoriteTropes || '—'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Reading History */}
              <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-slate-900">📖 Reading History</h3>
                  <Button variant="outline" size="sm" onClick={() => setShowReaderHistoryImport(!showReaderHistoryImport)}>
                    <Upload className="h-3.5 w-3.5 mr-1" />
                    {showReaderHistoryImport ? 'Hide Import' : 'Import History'}
                  </Button>
                </div>

                {readerReadingHistory.length > 0 ? (
                  <div className="space-y-4">
                    {/* Shelf counts */}
                    {(() => {
                      const read = readerReadingHistory.filter(b => b.readStatus === 'read');
                      const reading = readerReadingHistory.filter(b => b.readStatus === 'reading');
                      const tbr = readerReadingHistory.filter(b => b.readStatus === 'to-read');
                      const favourites = readerReadingHistory.filter(b => b.rating === 5);
                      return (
                        <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                          <div className="rounded-lg bg-teal-50 border border-teal-200 p-2 text-center">
                            <p className="text-lg font-bold text-teal-700">{readerReadingHistory.length}</p>
                            <p className="text-[8px] text-teal-600 uppercase font-semibold">All Books</p>
                          </div>
                          <div className="rounded-lg bg-green-50 border border-green-200 p-2 text-center">
                            <p className="text-lg font-bold text-green-700">{read.length}</p>
                            <p className="text-[8px] text-green-600 uppercase font-semibold">Read</p>
                          </div>
                          <div className="rounded-lg bg-blue-50 border border-blue-200 p-2 text-center">
                            <p className="text-lg font-bold text-blue-700">{reading.length}</p>
                            <p className="text-[8px] text-blue-600 uppercase font-semibold">Reading</p>
                          </div>
                          <div className="rounded-lg bg-slate-50 border border-slate-200 p-2 text-center">
                            <p className="text-lg font-bold text-slate-700">{tbr.length}</p>
                            <p className="text-[8px] text-slate-500 uppercase font-semibold">TBR</p>
                          </div>
                          <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-2 text-center">
                            <p className="text-lg font-bold text-yellow-700">{favourites.length}</p>
                            <p className="text-[8px] text-yellow-600 uppercase font-semibold">5-Star</p>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Top rated books */}
                    {(() => {
                      const topRated = readerReadingHistory
                        .filter(b => b.rating >= 4)
                        .sort((a, b) => (b.rating || 0) - (a.rating || 0))
                        .slice(0, 5);
                      if (topRated.length === 0) return null;
                      return (
                        <div>
                          <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Top Rated Books</p>
                          <div className="space-y-1">
                            {topRated.map(b => (
                              <div key={b.id} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 p-2 text-xs">
                                <div className="min-w-0">
                                  <p className="font-semibold text-slate-800 truncate">{b.title}</p>
                                  <p className="text-[10px] text-slate-500">{b.author}</p>
                                </div>
                                <span className="text-slate-500 flex-shrink-0 ml-2">{'⭐'.repeat(b.rating)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* TBR List */}
                    {(() => {
                      const tbr = readerReadingHistory.filter(b => b.readStatus === 'to-read').slice(0, 10);
                      if (tbr.length === 0) return null;
                      return (
                        <div>
                          <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1">TBR List (Want to Read)</p>
                          <p className="text-[10px] text-slate-400 mb-2">Books this reader wants to read — target them for ARC invites on these titles.</p>
                          <div className="space-y-1">
                            {tbr.map(b => (
                              <div key={b.id} className="flex items-center justify-between rounded-lg border border-slate-100 bg-yellow-50/50 p-2 text-xs">
                                <div className="min-w-0">
                                  <p className="font-semibold text-slate-800 truncate">{b.title}</p>
                                  <p className="text-[10px] text-slate-500">{b.author}</p>
                                </div>
                                <Badge className="bg-yellow-100 text-yellow-700 text-[9px]">TBR</Badge>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Import batches */}
                    {readerImportBatches.length > 0 && (
                      <div>
                        <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Import History</p>
                        <div className="space-y-1">
                          {readerImportBatches.map(batch => (
                            <div key={batch.batchId} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 p-2 text-xs">
                              <div>
                                <span className="font-semibold text-slate-700">{batch.platform}</span>{' '}
                                <span className="text-slate-500">
                                  — {batch.booksImported} books · {batch.importedAt?.toDate?.()?.toLocaleDateString() || '—'}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-3">No reading history imported yet.</p>
                )}

                {/* Import form */}
                {showReaderHistoryImport && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <ReadingHistoryImport
                      isAdmin={true}
                      readerId={viewingReader.id}
                      existingBooks={[]}
                      allReaders={readers}
                      adminUserId={user?.uid}
                      onComplete={() => loadReaderReadingHistory(viewingReader.id)}
                    />
                  </div>
                )}
              </div>

              {/* Reading Résumé */}
              <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5">
                <h3 className="mb-3 text-lg font-bold text-slate-900">📄 Reading Résumé</h3>
                <p className="text-xs text-slate-500 mb-3">Preview and download this reader's résumé — useful for evaluating ARC applications.</p>
                <ReaderResume readerData={viewingReader} isAdmin={true} />
              </div>

              {/* Manual Points & Rewards Management */}
              <div className="mb-6 rounded-2xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50 p-6">
                <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-purple-900">
                  <Gift className="h-5 w-5" /> Manual Points & Rewards
                </h3>

                {/* Points Display */}
                <div className="mb-4 rounded-lg bg-white p-4">
                  <p className="text-sm font-medium text-slate-600">Current Balance</p>
                  <p className="text-3xl font-bold text-purple-600">{viewingReader.totalPoints || 0} points</p>
                </div>

                {/* Add Points */}
                <div className="mb-4 rounded-lg border-2 border-green-200 bg-white p-4">
                  <p className="mb-3 text-sm font-bold text-green-900">➕ Add Bonus Points</p>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Points Amount</label>
                      <input type="number" min="1" placeholder="e.g., 50" value={pointsAmount}
                        onChange={(e) => setPointsAmount(Number(e.target.value))}
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Reason</label>
                      <input type="text" placeholder="e.g., Great Instagram engagement" value={pointsReason}
                        onChange={(e) => setPointsReason(e.target.value)}
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                    </div>
                    <button onClick={() => handleAddPoints(viewingReader.id, pointsAmount, pointsReason)}
                      className="w-full rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 px-4 py-2 text-sm font-bold text-white hover:from-green-700 hover:to-emerald-700">
                      Add {pointsAmount || 0} Points
                    </button>
                  </div>
                </div>

                {/* Deduct Points */}
                <div className="mb-4 rounded-lg border-2 border-red-200 bg-white p-4">
                  <p className="mb-3 text-sm font-bold text-red-900">➖ Deduct Points</p>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Points Amount</label>
                      <input type="number" min="1" placeholder="e.g., 20" value={pointsAmount}
                        onChange={(e) => setPointsAmount(Number(e.target.value))}
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Reason</label>
                      <input type="text" placeholder="e.g., Didn't post review as promised" value={pointsReason}
                        onChange={(e) => setPointsReason(e.target.value)}
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                    </div>
                    <button onClick={() => handleDeductPoints(viewingReader.id, pointsAmount, pointsReason)}
                      className="w-full rounded-lg bg-gradient-to-r from-red-600 to-rose-600 px-4 py-2 text-sm font-bold text-white hover:from-red-700 hover:to-rose-700">
                      Deduct {pointsAmount || 0} Points
                    </button>
                  </div>
                </div>

                {/* Give Manual Reward */}
                <div className="rounded-lg border-2 border-slate-200 bg-white p-4">
                  <p className="mb-3 text-sm font-bold text-slate-900">🎁 Give Manual Reward</p>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Select Reward</label>
                      <select value={selectedRewardId} onChange={(e) => setSelectedRewardId(e.target.value)}
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
                        <option value="">Choose a reward...</option>
                        {(rewards || []).filter(r => r.available).map(reward => (
                          <option key={reward.id} value={reward.id}>{reward.name} ({reward.pointCost} pts)</option>
                        ))}
                      </select>
                    </div>
                    <button onClick={() => handleGiveReward(viewingReader.id, selectedRewardId)}
                      disabled={!selectedRewardId}
                      className="w-full rounded-lg bg-gradient-to-r from-teal-600 to-orange-600 px-4 py-2 text-sm font-bold text-white hover:from-teal-700 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed">
                      Give Reward (Free - No Points Deducted)
                    </button>
                  </div>
                </div>
              </div>

              {/* Points History */}
              <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-6">
                <h3 className="mb-4 text-lg font-bold text-slate-900">📊 Points History</h3>
                {pointsHistory.length === 0 ? (
                  <p className="text-sm text-slate-500">No points history yet</p>
                ) : (
                  <div className="space-y-2">
                    {pointsHistory.map((entry) => (
                      <div key={entry.id}
                        className={`flex items-start justify-between rounded-lg border p-3 ${entry.amount > 0 ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                        <div>
                          <p className={`text-sm font-bold ${entry.amount > 0 ? 'text-green-900' : 'text-red-900'}`}>
                            {entry.amount > 0 ? '+' : ''}{entry.amount} points
                          </p>
                          <p className="text-xs text-slate-600">{entry.reason}</p>
                          <p className="text-xs text-slate-400">{entry.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown date'}</p>
                        </div>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          entry.type === 'manual_add' ? 'bg-green-200 text-green-800' :
                          entry.type === 'manual_deduct' ? 'bg-red-200 text-red-800' :
                          'bg-blue-200 text-blue-800'
                        }`}>
                          {entry.type === 'manual_add' ? 'Manual Add' : entry.type === 'manual_deduct' ? 'Manual Deduct' : 'Review'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Review History */}
              <div className="rounded-xl border border-slate-100 bg-white p-5">
                <h3 className="mb-4 text-lg font-bold text-slate-900">⭐ Review History</h3>
                {readerReviews.length === 0 ? (
                  <p className="text-center text-sm text-slate-400 py-8">No reviews submitted yet</p>
                ) : (
                  <div className="space-y-4">
                    {readerReviews.map((review) => (
                      <div key={review.id} className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="font-bold text-slate-900">{review.bookTitle}</h4>
                            <p className="text-xs text-slate-500">{review.submittedAt?.toDate().toLocaleDateString()}</p>
                          </div>
                          {review.pointsEarned && (
                            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-teal-700">+{review.pointsEarned} pts</span>
                          )}
                        </div>
                        {review.rating && (
                          <div className="mb-3"><span className="text-2xl text-slate-500">{'⭐'.repeat(review.rating)}</span></div>
                        )}
                        {review.quickSummary && (
                          <div className="mb-3 rounded-md bg-white p-3">
                            <p className="text-xs font-semibold text-slate-500 mb-1">Quick Summary:</p>
                            <p className="text-sm text-slate-700">{review.quickSummary}</p>
                          </div>
                        )}
                        {review.lovedMost && (
                          <div className="mb-3">
                            <p className="text-xs font-semibold text-slate-500 mb-1">💖 Loved Most:</p>
                            <p className="text-sm text-slate-700">{review.lovedMost}</p>
                            {review.lovedMostDetails && <p className="text-xs text-slate-600 mt-1 italic">{review.lovedMostDetails}</p>}
                          </div>
                        )}
                        {review.favoriteCharacterMoment && (
                          <div className="mb-3">
                            <p className="text-xs font-semibold text-slate-500 mb-1">✨ Favorite Moment:</p>
                            <p className="text-sm text-slate-700">{review.favoriteCharacterMoment}</p>
                          </div>
                        )}
                        {review.targetAudience?.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs font-semibold text-slate-500 mb-2">🎯 Perfect For:</p>
                            <div className="flex flex-wrap gap-2">
                              {review.targetAudience.map((audience, idx) => (
                                <span key={idx} className="rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-700">{audience}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {review.fullReview && (
                          <div className="mb-3">
                            <p className="text-xs font-semibold text-slate-500 mb-1">Full Review:</p>
                            <p className="text-sm text-slate-700 leading-relaxed">{review.fullReview}</p>
                          </div>
                        )}
                        {review.favoriteQuote && (
                          <div className="mb-3 rounded-md bg-slate-50 p-3 border-l-4 border-teal-400">
                            <p className="text-sm text-slate-900 italic">"{review.favoriteQuote}"</p>
                          </div>
                        )}
                        {review.privateNotes && (
                          <div className="mb-3 rounded-md bg-yellow-50 p-3 border-2 border-yellow-300">
                            <div className="flex items-center gap-2 mb-2">
                              <Lock className="h-4 w-4 text-yellow-700" />
                              <p className="text-xs font-bold text-yellow-900">PRIVATE NOTES (Author Only)</p>
                            </div>
                            <p className="text-sm text-yellow-900">{review.privateNotes}</p>
                          </div>
                        )}
                        {review.reviewLinks && (
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(review.reviewLinks)
                              .filter(([, link]) => link)
                              .map(([platform, link]) => (
                                <a key={platform} href={link} target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-200">
                                  {platform} <ExternalLink size={10} />
                                </a>
                              ))}
                          </div>
                        )}
                        {review.comments && (
                          <div className="mt-3 rounded-md bg-slate-100 p-2">
                            <p className="text-xs text-slate-600 italic">{review.comments}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Platform Stats */}
              <div className="rounded-xl border border-slate-100 bg-white p-5">
                <h3 className="mb-4 text-lg font-bold text-slate-900">Platform Statistics</h3>
                <div className="space-y-2 text-sm">
                  {['amazon', 'goodreads', 'instagram', 'tiktok', 'blog'].map(platform => {
                    const count = readerReviews.filter(r => r.reviewLinks?.[platform]).length;
                    return count > 0 ? (
                      <div key={platform} className="flex items-center justify-between">
                        <span className="text-slate-600 capitalize">{platform} Reviews:</span>
                        <span className="font-bold text-slate-900">{count}</span>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* ----------------------------------------
           READER DRAWER
      ---------------------------------------- */}
      {showReaderDrawer && (
        <div className="fixed inset-0 z-40 flex items-center justify-end bg-slate-900/30 backdrop-blur-sm">
          <div className="h-full w-full max-w-md overflow-y-auto border-l border-slate-100 bg-white px-4 py-4 shadow-2xl">

            {/* Drawer Header */}
            <div className="mb-3 flex items-center justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.26em] text-teal-700">
                  {editingReader ? 'Edit reader' : 'New reader'}
                </p>
                <h2 className="text-sm font-semibold text-slate-900">ARC reader details</h2>
              </div>
              <button onClick={closeReaderDrawer}
                className="rounded-full border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600 hover:text-teal-700">
                Close
              </button>
            </div>

            {/* Drawer Form */}
            <div className="space-y-3">

              {/* BASIC INFO */}
              <div className="rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2">
                <p className="mb-2 text-[11px] font-semibold text-slate-700">Basic information</p>
                <div className="space-y-2">
                  <div>
                    <label className="block text-[11px] mb-1 font-medium text-slate-600">Name *</label>
                    <input type="text" required value={readerForm.name}
                      onChange={(e) => setReaderForm({ ...readerForm, name: e.target.value })}
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]" />
                  </div>
                  <div>
                    <label className="block text-[11px] mb-1 font-medium text-slate-600">Email (correspondence) *</label>
                    <input type="email" required value={readerForm.emailCorrespondence}
                      onChange={(e) => setReaderForm({ ...readerForm, emailCorrespondence: e.target.value })}
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]" />
                  </div>
                  <div>
                    <label className="block text-[11px] mb-1 font-medium text-slate-600">Email (eReader)</label>
                    <input type="email" value={readerForm.eReaderEmail}
                      onChange={(e) => setReaderForm({ ...readerForm, eReaderEmail: e.target.value })}
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]" />
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={readerForm.acceptsMarketing}
                      onChange={(e) => setReaderForm({ ...readerForm, acceptsMarketing: e.target.checked })}
                      className="h-3.5 w-3.5 rounded border-slate-300 text-teal-600" />
                    <label className="text-[11px] text-slate-700">Accepts marketing emails</label>
                  </div>
                </div>
              </div>

              {/* ARC PREFERENCES */}
              <div className="rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2">
                <p className="mb-2 text-[11px] font-semibold text-slate-700">ARC preferences</p>
                <div className="space-y-2">
                  <div>
                    <label className="block text-[11px] mb-1 font-medium text-slate-600">Interested in</label>
                    <select value={readerForm.interestedIn}
                      onChange={(e) => setReaderForm({ ...readerForm, interestedIn: e.target.value })}
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]">
                      <option>eBook ARC request</option>
                      <option>Print ARC request</option>
                      <option>Both eBook and Print</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] mb-1 font-medium text-slate-600">Status</label>
                    <select value={readerForm.status}
                      onChange={(e) => setReaderForm({ ...readerForm, status: e.target.value })}
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]">
                      <option>Noted ARC interest</option>
                      <option>Engaged</option>
                      <option>Follow-up needed</option>
                      <option>Accepted</option>
                      <option>ARC sent</option>
                      <option>Reviewed</option>
                      <option>Closed</option>
                      <option>Street Team</option>
                      <option>Book 2 ARC interest</option>
                      <option>New Buyer</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[11px] mb-1 font-medium text-slate-600">Signup date</label>
                      <input type="date" value={readerForm.signupDate}
                        onChange={(e) => setReaderForm({ ...readerForm, signupDate: e.target.value })}
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]" />
                    </div>
                    <div>
                      <label className="block text-[11px] mb-1 font-medium text-slate-600">ARC sent (date)</label>
                      <input type="date" value={readerForm.arcSent}
                        onChange={(e) => setReaderForm({ ...readerForm, arcSent: e.target.value })}
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[11px] mb-1 font-medium text-slate-600">Review date</label>
                      <input type="date" value={readerForm.reviewDate}
                        onChange={(e) => setReaderForm({ ...readerForm, reviewDate: e.target.value })}
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]" />
                    </div>
                    <div>
                      <label className="block text-[11px] mb-1 font-medium text-slate-600">Rating</label>
                      <input type="number" min="0" max="5" step="0.1" value={readerForm.rating}
                        onChange={(e) => setReaderForm({ ...readerForm, rating: e.target.value })}
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]" />
                    </div>
                  </div>
                </div>
              </div>

              {/* BOOK ASSIGNMENTS */}
              <div className="rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2">
                <p className="mb-2 text-[11px] font-semibold text-slate-700">Book Assignments</p>
                <p className="mb-2 text-[10px] text-slate-500">Tick to create an independent reader record for that book. Untick to remove.</p>
                <div className="space-y-2">
                  {books.length === 0 ? (
                    <p className="text-[11px] text-slate-500">No books available. Add books first.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {books.map((book) => {
                        // Check if a per-book record exists for this reader
                        const pbReaders = perBookReaders[book.id] || [];
                        const readerId = editingReader?.id || '';
                        const readerEmail = readerForm.emailCorrespondence || '';
                        const masterRef = editingReader?.masterReaderId || readerId;
                        // Find matching per-book record (by ID, email, or masterReaderId)
                        const matchingPbRecord = pbReaders.find(r =>
                          r.id === readerId ||
                          r.id === masterRef ||
                          (r.emailCorrespondence && readerEmail && r.emailCorrespondence.toLowerCase() === readerEmail.toLowerCase()) ||
                          (r.masterReaderId && (r.masterReaderId === readerId || r.masterReaderId === masterRef))
                        );
                        // Also check the global reader's booksReceived array
                        const globalReader = readers.find(r =>
                          r.id === readerId ||
                          r.id === masterRef ||
                          (r.emailCorrespondence && readerEmail && r.emailCorrespondence.toLowerCase() === readerEmail.toLowerCase())
                        );
                        const inGlobalBooksReceived = globalReader && (globalReader.booksReceived || []).includes(book.id);
                        const hasPerBookRecord = !!matchingPbRecord || !!inGlobalBooksReceived;
                        const isCurrent = book.id === editingBookId;
                        // Check campaign applications
                        const apps = bookApplications[book.id] || [];
                        const readerApp = apps.find(a =>
                          (a.readerId && (a.readerId === readerId || a.readerId === masterRef)) ||
                          (a.readerEmail && readerEmail && a.readerEmail.toLowerCase() === readerEmail.toLowerCase())
                        );
                        const isWaitlisted = readerApp && readerApp.status === 'waitlisted';
                        const isPending = readerApp && (readerApp.status === 'pending' || readerApp.status === 'interested');

                        return (
                          <div key={book.id}>
                            <label className="flex items-center gap-2">
                              <input type="checkbox"
                                checked={hasPerBookRecord}
                                onChange={async (e) => {
                                  const docId = masterRef || readerEmail;
                                  if (!docId) return;
                                  try {
                                    if (e.target.checked && !hasPerBookRecord) {
                                      // Create independent clone for this book
                                      if (basePath) {
                                        await setDoc(doc(db, basePath, 'books', book.id, 'readers', docId), {
                                          name: readerForm.name || '',
                                          emailCorrespondence: readerForm.emailCorrespondence || '',
                                          eReaderEmail: readerForm.eReaderEmail || '',
                                          source: readerForm.source || 'manual',
                                          acceptsMarketing: readerForm.acceptsMarketing ?? true,
                                          status: 'Noted ARC interest',
                                          hasApprovedArcRequest: false,
                                          arcSent: '', reviewDate: '', rating: '', reviewRating: '',
                                          totalPoints: 0, totalReviews: 0,
                                          signupDate: readerForm.signupDate || new Date().toISOString().split('T')[0],
                                          notes: '',
                                          interestedIn: readerForm.interestedIn || 'eBook ARC request',
                                          reviewPlatforms: readerForm.reviewPlatforms || [],
                                          socialUsernames: readerForm.socialUsernames || '',
                                          primaryPlatform: readerForm.primaryPlatform || 'Instagram',
                                          followerCount: readerForm.followerCount || 0,
                                          location: readerForm.location || '',
                                          favoriteBooks: readerForm.favoriteBooks || '',
                                          bookId: book.id,
                                          masterReaderId: masterRef || readerEmail,
                                          createdAt: serverTimestamp(),
                                          lastModified: serverTimestamp(),
                                        });
                                      }
                                      // Also sync global reader's booksReceived
                                      if (globalReader) {
                                        const currentBooks = globalReader.booksReceived || [];
                                        if (!currentBooks.includes(book.id)) {
                                          await updateDoc(doc(db, 'readers', globalReader.id), {
                                            booksReceived: [...currentBooks, book.id],
                                            lastModified: serverTimestamp(),
                                          });
                                        }
                                      }
                                    } else if (!e.target.checked && hasPerBookRecord && !isCurrent) {
                                      // Delete per-book record using the actual matching doc's ID
                                      if (basePath && matchingPbRecord) {
                                        await deleteDoc(doc(db, basePath, 'books', book.id, 'readers', matchingPbRecord.id));
                                      } else if (basePath) {
                                        // Fallback: try with the computed docId
                                        await deleteDoc(doc(db, basePath, 'books', book.id, 'readers', docId));
                                      }
                                      // Also remove from global reader's booksReceived
                                      if (globalReader) {
                                        const currentBooks = globalReader.booksReceived || [];
                                        if (currentBooks.includes(book.id)) {
                                          await updateDoc(doc(db, 'readers', globalReader.id), {
                                            booksReceived: currentBooks.filter(id => id !== book.id),
                                            lastModified: serverTimestamp(),
                                          });
                                        }
                                      }
                                    }
                                  } catch (err) {
                                    console.error('[AuthorReadersTab] Book assignment error:', err);
                                    alert('Error updating book assignment: ' + err.message);
                                  }
                                }}
                                disabled={isCurrent && hasPerBookRecord}
                                className="h-3.5 w-3.5 rounded border-slate-300 text-teal-600 focus:ring-slate-500" />
                              <span className="text-[11px] text-slate-700">{book.title}</span>
                              {isCurrent && (
                                <span className="inline-flex rounded-full bg-teal-100 px-1.5 py-0.5 text-[9px] font-medium text-teal-700">Current</span>
                              )}
                              {hasPerBookRecord && !isCurrent && (
                                <span className="inline-flex rounded-full bg-green-100 px-1.5 py-0.5 text-[9px] font-medium text-green-700">Assigned</span>
                              )}
                            </label>
                            {(isWaitlisted || isPending) && !hasPerBookRecord && (
                              <div className="ml-6 mt-0.5 flex items-center gap-1.5">
                                <Clock className="h-3 w-3 text-yellow-600" />
                                <span className="text-[10px] font-medium text-yellow-700">
                                  {isWaitlisted ? 'Waitlisted' : 'Pending application'}
                                </span>
                                {readerApp.appliedAt?.toDate && (
                                  <span className="text-[9px] text-slate-400">
                                    since {readerApp.appliedAt.toDate().toLocaleDateString('en-GB')}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* SOCIAL + NOTES */}
              <div className="rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2">
                <p className="mb-2 text-[11px] font-semibold text-slate-700">Socials & notes</p>
                <div className="space-y-2">
                  <div>
                    <label className="block text-[11px] mb-1 font-medium text-slate-600">Review platforms</label>
                    <input type="text"
                      value={Array.isArray(readerForm.reviewPlatforms) ? readerForm.reviewPlatforms.join(', ') : ''}
                      onChange={(e) => setReaderForm({ ...readerForm, reviewPlatforms: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]"
                      placeholder="Amazon, Goodreads…" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[11px] mb-1 font-medium text-slate-600">Primary Platform</label>
                      <select value={readerForm.primaryPlatform}
                        onChange={(e) => setReaderForm({ ...readerForm, primaryPlatform: e.target.value })}
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]">
                        <option>Instagram</option>
                        <option>TikTok</option>
                        <option>YouTube</option>
                        <option>Blog</option>
                        <option>Goodreads</option>
                        <option>BookBub</option>
                        <option>Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[11px] mb-1 font-medium text-slate-600">Follower Count</label>
                      <input type="number" min="0" value={readerForm.followerCount}
                        onChange={(e) => setReaderForm({ ...readerForm, followerCount: parseInt(e.target.value) || 0 })}
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]"
                        placeholder="5000" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] mb-1 font-medium text-slate-600">Social usernames</label>
                    <input type="text" value={readerForm.socialUsernames}
                      onChange={(e) => setReaderForm({ ...readerForm, socialUsernames: e.target.value })}
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]"
                      placeholder="@user on IG…" />
                  </div>
                  <div>
                    <label className="block text-[11px] mb-1 font-medium text-slate-600">Favourite books</label>
                    <input type="text" value={readerForm.favoriteBooks}
                      onChange={(e) => setReaderForm({ ...readerForm, favoriteBooks: e.target.value })}
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]" />
                  </div>
                  <div>
                    <label className="block text-[11px] mb-1 font-medium text-slate-600">Location</label>
                    <input type="text" value={readerForm.location}
                      onChange={(e) => setReaderForm({ ...readerForm, location: e.target.value })}
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]" />
                  </div>
                  <div>
                    <label className="block text-[11px] mb-1 font-medium text-slate-600">Notes</label>
                    <textarea rows={3} value={readerForm.notes}
                      onChange={(e) => setReaderForm({ ...readerForm, notes: e.target.value })}
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]" />
                  </div>
                </div>
              </div>

              {/* SAVE BAR */}
              <div className="flex items-center justify-between gap-2 pt-1">
                <button type="button" disabled={isSavingReader} onClick={handleSaveReader}
                  className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-1.5 text-[11px] font-semibold text-white hover:bg-teal-700 disabled:opacity-70">
                  {isSavingReader ? 'Saving…' : 'Save reader'}
                </button>
                <button type="button" onClick={closeReaderDrawer}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px] text-slate-700 hover:text-teal-700">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QUICK ADD MODAL */}
      {showQuickAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-2xl rounded-2xl border border-slate-100 bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-slate-900">Quick Add from Email</h3>
                <p className="text-xs text-slate-500">Paste the entire signup email below</p>
              </div>
              <button onClick={() => { setShowQuickAddModal(false); setPastedEmailText(''); }}
                className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                <XCircle size={20} />
              </button>
            </div>
            <div className="mb-4">
              <label className="mb-2 block text-xs font-medium text-slate-600">Paste Email Content:</label>
              <textarea value={pastedEmailText} onChange={(e) => setPastedEmailText(e.target.value)}
                rows={12}
                placeholder={`Name: Jane Doe\nEmail (Correspondence): janedoe@gmail.com, accepts marketing: false\nEmail (eReaders only): janedow@kindle.com\nWhat are you interested in?: eBook ARC request\nWhere do you currently leave book reviews?: Instagram, TikTok, Amazon, Goodreads\n...`}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200 font-mono" />
            </div>
            <div className="flex gap-3">
              <button onClick={handleQuickAddFromEmail}
                disabled={!pastedEmailText.trim() || isParsingEmail}
                className="flex-1 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed">
                {isParsingEmail ? 'Parsing with AI...' : 'Add Reader'}
              </button>
              <button onClick={() => { setShowQuickAddModal(false); setPastedEmailText(''); }}
                disabled={isParsingEmail}
                className="flex-1 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* SEND ARC MODAL */}
      {showSendArcModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-slate-100 bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold text-slate-900">Send ARC Email</h3>
              <button onClick={() => setShowSendArcModal(false)} className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Recipients */}
            <div className="mb-4">
              {(() => {
                const allPB = Object.values(perBookReaders).flat();
                const selected = allPB.filter(r => selectedReaderIds.has(r.id));
                // Deduplicate by id (a reader may appear in multiple books)
                const seen = new Set();
                const unique = selected.filter(r => { if (seen.has(r.id)) return false; seen.add(r.id); return true; });
                const kindleCount = unique.filter(r => getKindleEmail(r, globalKindleMap).toLowerCase().endsWith('@kindle.com')).length;
                const noKindleCount = unique.length - kindleCount;

                return (
                  <>
                    <div className="mb-2 flex items-center gap-3">
                      <p className="text-sm font-semibold text-slate-700">Recipients ({unique.length})</p>
                      <span className="text-[10px] text-green-600 font-medium">{kindleCount} deliverable</span>
                      {noKindleCount > 0 && (
                        <span className="text-[10px] text-orange-600 font-medium">{noKindleCount} no Kindle email — will be skipped</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5 rounded-lg border border-slate-200 bg-slate-50 p-2 max-h-24 overflow-y-auto">
                      {unique.map(r => {
                        const hasKindle = getKindleEmail(r, globalKindleMap).toLowerCase().endsWith('@kindle.com');
                        return (
                          <span key={r.id} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-medium ${
                            hasKindle ? 'bg-teal-100 text-teal-700' : 'bg-orange-100 text-orange-700 line-through'
                          }`}>
                            {!hasKindle && <AlertTriangle className="h-2.5 w-2.5" />}
                            {r.name || r.emailCorrespondence}
                            <button onClick={() => toggleReaderSelection(r.id)} className="ml-0.5 rounded-full p-0.5 hover:bg-teal-200">
                              <X className="h-2.5 w-2.5" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Book selection */}
            <div className="mb-4">
              <label className="mb-1 block text-sm font-semibold text-slate-700">Book</label>
              <select value={arcBookId} onChange={(e) => handleArcBookChange(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-200">
                {books.map(b => (
                  <option key={b.id} value={b.id}>{b.title}</option>
                ))}
              </select>
            </div>

            {/* ePUB upload */}
            <div className="mb-4">
              <label className="mb-1 block text-sm font-semibold text-slate-700">Attach ePUB</label>
              {arcEpubFile ? (
                <div className="flex items-center gap-2 rounded-lg border border-teal-200 bg-teal-50 px-3 py-2">
                  <Paperclip className="h-4 w-4 text-teal-600" />
                  <span className="flex-1 text-sm text-teal-800 truncate">{arcEpubFile.name}</span>
                  <span className="text-xs text-teal-600">{(arcEpubFile.size / 1024 / 1024).toFixed(1)} MB</span>
                  <button onClick={() => setArcEpubFile(null)} className="rounded-full p-0.5 text-teal-600 hover:bg-teal-100">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <button onClick={() => epubInputRef.current?.click()}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-500 hover:border-teal-400 hover:bg-teal-50 hover:text-teal-600 transition-colors">
                  <Upload className="h-4 w-4" /> Choose ePUB file
                </button>
              )}
              <input ref={epubInputRef} type="file" accept=".epub" className="hidden"
                onChange={(e) => { if (e.target.files?.[0]) setArcEpubFile(e.target.files[0]); }}
              />
              <p className="mt-1 text-[10px] text-slate-400">Optional — the ePUB will be attached to the email</p>
            </div>

            {/* Subject */}
            <div className="mb-4">
              <label className="mb-1 block text-sm font-semibold text-slate-700">Subject</label>
              <input type="text" value={arcSubject} onChange={(e) => setArcSubject(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
              />
            </div>

            {/* Body */}
            <div className="mb-4">
              <label className="mb-1 block text-sm font-semibold text-slate-700">
                Email Body
                <span className="ml-2 text-[10px] font-normal text-slate-400">
                  Use {'{readerFirstName}'} or {'{readerName}'} for personalisation
                </span>
              </label>
              <textarea value={arcBody} onChange={(e) => setArcBody(e.target.value)} rows={8}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-200 leading-relaxed"
              />
            </div>

            {/* Progress */}
            {arcUploadProgress && (
              <div className="mb-4 rounded-lg bg-blue-50 px-4 py-2 text-sm text-blue-700">
                {arcUploadProgress}
              </div>
            )}
            {arcSendProgress && (
              <div className="mb-4 rounded-lg border border-teal-200 bg-teal-50 px-4 py-3 space-y-2">
                <div className="flex items-center justify-between text-sm font-medium text-teal-800">
                  <span>Sending {arcSendProgress.current} of {arcSendProgress.total}...</span>
                  <span>{arcSendProgress.sent} sent{arcSendProgress.failed > 0 ? `, ${arcSendProgress.failed} failed` : ''}</span>
                </div>
                <div className="h-2 w-full rounded-full bg-teal-100 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-teal-600 transition-all duration-300"
                    style={{ width: `${Math.round((arcSendProgress.current / arcSendProgress.total) * 100)}%` }}
                  />
                </div>
                {arcSendProgress.skipped > 0 && (
                  <p className="text-[10px] text-orange-600">{arcSendProgress.skipped} reader(s) skipped — no @kindle.com email</p>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button onClick={handleSendArc} disabled={isSendingArc || selectedReaderIds.size === 0}
                className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-teal-600 py-3 text-sm font-bold text-white hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                <Send className="h-4 w-4" />
                {isSendingArc ? 'Sending...' : `Send to ${selectedReaderIds.size} reader${selectedReaderIds.size !== 1 ? 's' : ''}`}
              </button>
              <button onClick={() => setShowSendArcModal(false)} disabled={isSendingArc}
                className="rounded-lg border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <KindleMissingDialog
        open={!!kindleMissingPrompt}
        readerNames={kindleMissingPrompt?.skippedNames || []}
        onCancel={() => setKindleMissingPrompt(null)}
        onProceed={kindleMissingPrompt?.onProceed || undefined}
        proceedLabel="Send to readers with Kindle email"
      />
    </>
  );
}
